/* ============================================================
   VITALCASA CRM — ModalRevisarCaptacion
   Modal para validar una captación en estado "Para Revisar"
   y convertirla en propiedad con Ref.
   ============================================================ */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { useTheme } from './ThemeProvider';
import VisorImagenDocumento from './captaciones/VisorImagenDocumento';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { postCaptacionMultipart } from '../services/captacionUpload';
import {
  parseEuroAmount,
  parsePercentFlexible,
  parsePrecioVentaEntero,
  formatPrecioVentaEntero,
  formatEuroConSimbolo,
  formatPrecioVentaConEuro,
  roundEuro2,
} from '../utils/moneyParse';
import {
  getAuxSlugForCampoKey,
  normalizeAuxSelectValue,
  ordenarOpcionesTipoEquipoDesplegable,
  buildAuxExtrasPatch,
  normalizeVistasIdsFromExtras,
  buildVistasExtrasPatch,
  isVistasMultiCampo,
} from '../config/auxCamposPipeline';
import { useAuxListsForCampos } from '../hooks/useAuxListsForCampos';
import { tipoInmuebleLegible, refPipelineCard, origenMultimediaCaptacion, esCaptacionPrueba, direccionPipelineCard } from '../utils/captacionDisplay';
import { esTituloDePortalScraping, tituloCrmUsable } from '../utils/tituloPortalScraping';
import { esParcelaBasuraTipo, normalizarDireccionRusticaCaptacion } from '../utils/parseDireccionRustica';
import { mapFotoCaptacionDesdeApi, esFotoRawSinPreview, fotoTieneThumbGrid } from '../utils/fotoMultimediaUtils';
import CaptacionFotoThumb from './CaptacionFotoThumb';
import { crearMiniaturaDesdeFile } from '../utils/fotoThumbUtils';
import { buildContextoPropiedadIA } from '../utils/buildContextoPropiedadIA';
import { IconoLupa } from './gestionVentas/ModalVistaPreviaDocumentoNas';
import LightboxParrillaSortableCell from './captacion/LightboxParrillaSortableCell';
import ModalPortalesAltaWeb from './propiedades/ModalPortalesAltaWeb';
import usePermiso from '../hooks/usePermiso';

const CALIFICACIONES = [
  { valor: 0, label: '0★', color: '#6b7280' },
  { valor: 1, label: '1★', color: '#f59e0b' },
  { valor: 2, label: '2★', color: '#f97316' },
  { valor: 3, label: '3★', color: '#22c55e' },
];

const PROVINCIAS_ES = [
  { id: '01', nombre: 'Álava' }, { id: '02', nombre: 'Albacete' }, { id: '03', nombre: 'Alicante' },
  { id: '04', nombre: 'Almería' }, { id: '05', nombre: 'Ávila' }, { id: '06', nombre: 'Badajoz' },
  { id: '07', nombre: 'Baleares' }, { id: '08', nombre: 'Barcelona' }, { id: '09', nombre: 'Burgos' },
  { id: '10', nombre: 'Cáceres' }, { id: '11', nombre: 'Cádiz' }, { id: '12', nombre: 'Castellón' },
  { id: '13', nombre: 'Ciudad Real' }, { id: '14', nombre: 'Córdoba' }, { id: '15', nombre: 'A Coruña' },
  { id: '16', nombre: 'Cuenca' }, { id: '17', nombre: 'Girona' }, { id: '18', nombre: 'Granada' },
  { id: '19', nombre: 'Guadalajara' }, { id: '20', nombre: 'Guipúzcoa' }, { id: '21', nombre: 'Huelva' },
  { id: '22', nombre: 'Huesca' }, { id: '23', nombre: 'Jaén' }, { id: '24', nombre: 'León' },
  { id: '25', nombre: 'Lleida' }, { id: '26', nombre: 'La Rioja' }, { id: '27', nombre: 'Lugo' },
  { id: '28', nombre: 'Madrid' }, { id: '29', nombre: 'Málaga' }, { id: '30', nombre: 'Murcia' },
  { id: '31', nombre: 'Navarra' }, { id: '32', nombre: 'Ourense' }, { id: '33', nombre: 'Asturias' },
  { id: '34', nombre: 'Palencia' }, { id: '35', nombre: 'Las Palmas' }, { id: '36', nombre: 'Pontevedra' },
  { id: '37', nombre: 'Salamanca' }, { id: '38', nombre: 'S.C. Tenerife' }, { id: '39', nombre: 'Cantabria' },
  { id: '40', nombre: 'Segovia' }, { id: '41', nombre: 'Sevilla' }, { id: '42', nombre: 'Soria' },
  { id: '43', nombre: 'Tarragona' }, { id: '44', nombre: 'Teruel' }, { id: '45', nombre: 'Toledo' },
  { id: '46', nombre: 'Valencia' }, { id: '47', nombre: 'Valladolid' }, { id: '48', nombre: 'Vizcaya' },
  { id: '49', nombre: 'Zamora' }, { id: '50', nombre: 'Zaragoza' }, { id: '51', nombre: 'Ceuta' }, { id: '52', nombre: 'Melilla' },
];
const hoyISO = () => new Date().toISOString().slice(0, 10);
const CAMPOS_EXCLUSIVA_DEP = ['exclusiva_duracion', 'duracion_exclusiva', 'exclusiva_desde', 'exclusiva_hasta'];
const OPCIONES_OPERACION = ['Venta', 'Alquiler', 'Traspaso'];
const HON_MERGE = ['precio_propietario', 'honorarios_porcentaje', 'honorarios_euros', 'iva_porcentaje', 'iva_euros', 'total_honorarios'];
const SEED_EXTRAS_KEYS = ['titulo', 'web_titulo', 'web_desc_corta_es', 'web_desc_larga_es', 'referencia', 'referencia_manual', ...HON_MERGE];
const CAMPOS_WEB_SECRETARIA = new Set(['titulo', 'web_titulo', 'web_desc_corta_es', 'web_desc_larga_es']);

function buildFormCaptacionParaIA(cap, extras, valoresExtras, tiposInmueble) {
  const merged = { ...extras, ...valoresExtras };
  const esCodigoNumerico = (v) => /^\d{4,5}$/.test(String(v || '').trim());
  const pobNombre = (() => {
    for (const c of [merged.poblacion_nombre, merged.poblacion, cap?.poblacion]) {
      const s = String(c || '').trim();
      if (s && !esCodigoNumerico(s)) return s;
    }
    return '';
  })();
  return {
    ...merged,
    referencia: merged.referencia || merged.referencia_manual,
    titulo: tituloCaptacion(merged),
    web_titulo: merged.web_titulo || merged.titulo,
    tipo_inmueble_id: cap?.tipo_inmueble || merged.tipo_inmueble_id,
    operacion: cap?.operacion || merged.operacion,
    poblacion: pobNombre || merged.poblacion || cap?.poblacion,
    poblacion_nombre: pobNombre || merged.poblacion_nombre || '',
    num_dormitorios: merged.num_dormitorios ?? merged.dormitorios,
    num_banos: merged.num_banos ?? merged.banos,
    precio_venta: cap?.precio_venta ?? merged.precio_venta,
  };
}

function auxiliaresParaIACaptacion(auxLists, tiposInmueble) {
  return { ...auxLists, 'tipo-inmueble': tiposInmueble || [] };
}

function parseExtrasCap(captacion) {
  if (!captacion) return {};
  if (typeof captacion.datos_extras === 'object' && captacion.datos_extras !== null) return captacion.datos_extras;
  try { return JSON.parse(captacion.datos_extras || '{}'); } catch { return {}; }
}

function parseCalificacionCaptacion(captacion) {
  const extras = parseExtrasCap(captacion);
  const raw = captacion?.calificacion ?? extras.calificacion;
  // null/'' = sin elegir; 0★ Chollo es valor válido.
  if (raw === null || raw === undefined || raw === '') return null;
  const n = parseInt(String(raw), 10);
  if (Number.isFinite(n) && n >= 0 && n <= 3) return n;
  return null;
}

function tituloCaptacion(extras = {}) {
  // Preferir web_titulo limpio; nunca devolver título de portal (calle).
  return tituloCrmUsable(extras.web_titulo, extras)
    || tituloCrmUsable(extras.titulo, extras);
}

function formatConformeEtiqueta(fecha, nombre, rol) {
  if (!fecha) return '';
  const ts = new Date(fecha).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
  const quien = nombre ? ` (${nombre})` : '';
  return `Aceptado por ${rol}${quien} · ${ts}`;
}

const API_ORIGIN_CAP =
  typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL
    ? String(import.meta.env.VITE_API_URL).replace(/\/$/, '')
    : 'http://localhost:4000';

function absolutizarMediaUrl(u) {
  if (!u || typeof u !== 'string') return '';
  const t = u.trim();
  if (!t) return '';
  if (/^https?:\/\//i.test(t)) return t;
  return API_ORIGIN_CAP + (t.startsWith('/') ? '' : '/') + t;
}

/**
 * Miniatura de vídeo: prioriza url_thumb; si falta o falla la imagen,
 * muestra el propio <video> (primer frame H.264) en lugar del icono vacío.
 */
function VideoThumbButton({
  videoUrl,
  thumbUrl,
  onClick,
  width = 80,
  height = 58,
  border,
  playFontSize = 10,
}) {
  const src = absolutizarMediaUrl(videoUrl);
  const poster = absolutizarMediaUrl(thumbUrl);
  const [imgOk, setImgOk] = useState(!!poster);
  useEffect(() => {
    setImgOk(!!poster);
  }, [poster]);

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        position: 'relative',
        width,
        height,
        borderRadius: 8,
        overflow: 'hidden',
        border: `1px solid ${border}`,
        padding: 0,
        cursor: 'pointer',
        background: '#0f172a',
      }}
    >
      {poster && imgOk ? (
        <img
          src={poster}
          alt=""
          onError={() => setImgOk(false)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }}
        />
      ) : src ? (
        <video
          src={src}
          muted
          playsInline
          preload="metadata"
          style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none', background: '#0f172a' }}
        />
      ) : (
        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#1e293b,#0f172a)', fontSize: 18 }}>🎬</div>
      )}
      <span style={{ position: 'absolute', bottom: 4, left: 4, fontSize: playFontSize, fontWeight: 800, color: '#fff', textShadow: '0 1px 2px #000' }}>▶</span>
    </button>
  );
}

function docVistaPreviaKind(doc, urlFallback = '') {
  const mime = String(doc?.mime || '').toLowerCase();
  const name = String(doc?.nombre_documento || doc?.nombre_original || urlFallback || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime === 'application/pdf') return 'pdf';
  if (name.endsWith('.pdf')) return 'pdf';
  if (/\.(jpe?g|png|webp|gif)$/.test(name)) return 'image';
  return 'other';
}

const TIPOS_ENCARGO_DOC = new Set(['nota_encargo', 'contrato_exclusiva']);

function esDocFichaCaptacion(d) {
  if (!d) return false;
  if (d.tipo_codigo === 'ficha_captacion') return true;
  const nom = String(d.nombre_documento || d.nombre_original || d.nombre_archivo || '').toLowerCase();
  return /ficha\s*de\s*captaci/.test(nom);
}

function esDocCertGraficaCatastral(d) {
  if (!d) return false;
  if (d.tipo_codigo === 'cert_grafica_catastral') return true;
  const nom = String(d.nombre_documento || d.nombre_original || d.nombre_archivo || '').toLowerCase();
  return /cert\.?\s*gr[aá]fica\s*catastral/.test(nom);
}

const isMoneyField = (k = '') => {
  const key = String(k).toLowerCase();
  return (key.includes('precio') || key.includes('importe') || key.includes('valor')) && !key.includes('porcentaje');
};

/** Inputs de precio entero (sin céntimos): digitos → número. No usar para honorarios/IVA. */
const parseMoney = (value) => {
  const n = parsePrecioVentaEntero(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Visualización €. Números con céntimos (honorarios/IVA) van por formatEuroConSimbolo;
 * no pasar por strip de dígitos (12396.69 → 1239669 → «1.239.669 €»).
 */
const formatMoney = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (!Number.isInteger(value)) return formatEuroConSimbolo(value);
    return formatPrecioVentaConEuro(value);
  }
  const asEuro = parseEuroAmount(value);
  if (Number.isFinite(asEuro) && !Number.isInteger(asEuro)) return formatEuroConSimbolo(asEuro);
  return formatPrecioVentaConEuro(value);
};
const isCalificacionKey = (k = '') => String(k).toLowerCase().includes('calificacion');

// Tipo de input a renderizar para un campo
function getTipoCampo(campo_key, tipo_campo) {
  const k = campo_key.toLowerCase();
  if (['disponible_desde', 'exclusiva_desde', 'exclusiva_hasta'].includes(k)) return 'date';
  if (tipo_campo && ['boolean', 'toggle', 'checkbox'].includes(tipo_campo)) return 'boolean';
  if (['publicar_web', 'firma_captacion', 'exclusiva', 'disponible', 'visible_web', 'amueblado', 'vpo']
      .some(n => k === n || k.includes(n.replace('_', '')))) return 'boolean';
  if (tipo_campo && ['numero', 'decimal', 'integer'].includes(tipo_campo)) return 'number';
  if (['precio', 'honorarios', 'm2', 'metros', 'superficie', 'habitaciones', 'banos',
       'planta', 'antiguedad'].some(n => k.includes(n))) return 'number';
  if (tipo_campo === 'fecha' || k.includes('fecha')) return 'date';
  if (tipo_campo === 'textarea' || ['resumen', 'descripcion', 'notas', 'observaciones']
      .some(n => k.includes(n))) return 'textarea';
  return 'text';
}

export default function ModalRevisarCaptacion({ isOpen, captacion, onClose, onValidado, onOpenFicha, onOpenFichaOverlay }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === 'dark';
  const { puede } = usePermiso();
  const { usuario } = useAuth();
  // Ampliar: el agente dueño puede corregir campos de su ficha en Para Revisar
  const esCaptacionPropia = !!(
    usuario?.agente_id
    && captacion?.agente_id
    && String(usuario.agente_id) === String(captacion.agente_id)
  );
  const verRevFotosSec = puede('pl_capt_revisar_fotos_sec', 'ver');
  const modRevFotosSec = puede('pl_capt_revisar_fotos_sec', 'modificar');
  const verRevPublicar = puede('pl_capt_revisar_publicar', 'ver');
  const modRevPublicar = puede('pl_capt_revisar_publicar', 'modificar');
  const verRevCamposObl = puede('pl_capt_revisar_campos_obl', 'ver') || esCaptacionPropia;
  const modRevCamposObl = puede('pl_capt_revisar_campos_obl', 'modificar');
  const puedeEditarCamposAgente = modRevCamposObl || esCaptacionPropia;
  const verRevBorrador = puede('pl_capt_revisar_borrador', 'ver') || esCaptacionPropia;
  const verRevTextosWeb = puede('pl_capt_revisar_textos_web', 'ver');
  const modRevTextosWeb = puede('pl_capt_revisar_textos_web', 'modificar');
  const verRevConfSec = puede('pl_capt_revisar_conf_sec', 'ver');
  const verRevPrecioHon = puede('pl_capt_revisar_precio_hon', 'ver');
  const modRevPrecioHon = puede('pl_capt_revisar_precio_hon', 'modificar');
  const verRevCalificacion = puede('pl_capt_revisar_calificacion', 'ver');
  const modRevCalificacion = puede('pl_capt_revisar_calificacion', 'modificar');
  const verRevConfDir = puede('pl_capt_revisar_conf_dir', 'ver');
  const verRevFrio = puede('pl_capt_revisar_frio', 'ver');
  const verRevEliminar = puede('pl_capt_revisar_eliminar', 'ver');
  const tituloSinPermisoRev = 'No tienes permiso para modificar este campo';

  /** Retroceso + modal de motivo solo al cerrar Para Revisar, no al editar fotos inline. */
  const aplicarRetrocesoRespuesta = useCallback(async (res) => {
    const debeMotivo = res?.data?.retrocedido || res?.data?.necesitaMotivo;
    if (!debeMotivo) return false;
    onValidado?.({
      retrocedido: true,
      mensaje: res.data.mensajeRetroceso,
      motivoRetroceso: res.data.motivo,
      camposFaltantes: res.data.camposFaltantes,
    });
    onClose?.();
    return true;
  }, [onValidado, onClose]);

  const [camposObligatorios, setCamposObligatorios] = useState([]);
  const [camposCompletos, setCamposCompletos] = useState([]);
  const [valoresExtras, setValoresExtras] = useState({});   // inputs inline para campos faltantes
  const valoresExtrasRef = useRef({});
  useEffect(() => { valoresExtrasRef.current = valoresExtras; }, [valoresExtras]);
  const [agentes, setAgentes] = useState([]);
  const [poblaciones, setPoblaciones] = useState([]);
  const [tiposVia, setTiposVia] = useState([]);
  const [tiposInmueble, setTiposInmueble] = useState([]);
  const [guardandoExtras, setGuardandoExtras] = useState(false);
  const [guardadoExtrasOk, setGuardadoExtrasOk] = useState(false);
  const [modalPortalesAltaOpen, setModalPortalesAltaOpen] = useState(false);
  const [precioVentaStr, setPrecioVentaStr] = useState('');
  const [calificacion, setCalificacion] = useState(null);
  const [fotosReportaje, setFotosReportaje] = useState([]);
  const [subiendoFotos, setSubiendoFotos] = useState(false);
  const [fotoDragIdx, setFotoDragIdx] = useState(null);
  const inputFotoRef = useRef(null);
  const lightboxParrillaSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );
  const visorVideoRef = useRef(null);

  // ── Drag del modal ───────────────────────────────────────────
  const [pos, setPos] = useState(null);
  const dragRef = useRef(null);
  const dragOffsetRef = useRef(null);
  const startDrag = (e) => {
    if (e.target.closest('button')) return;
    const rect = dragRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragOffsetRef.current = { ox: e.clientX - rect.left, oy: e.clientY - rect.top, w: rect.width, h: rect.height };
    document.body.style.userSelect = 'none';
    const onMove = (ev) => {
      if (!dragOffsetRef.current) return;
      const { ox, oy, w, h } = dragOffsetRef.current;
      setPos({ x: Math.max(0, Math.min(window.innerWidth - w, ev.clientX - ox)), y: Math.max(0, Math.min(window.innerHeight - h, ev.clientY - oy)) });
    };
    const onUp = () => { dragOffsetRef.current = null; document.body.style.userSelect = ''; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };
  const [error, setError] = useState('');
  const [camposFaltantes, setCamposFaltantes] = useState(null);
  const [validado, setValidado] = useState(false);
  const [referencia, setReferencia] = useState('');
  const [accionArchivo, setAccionArchivo] = useState(null);
  const [motivoArchivo, setMotivoArchivo] = useState('');
  const [archivando, setArchivando] = useState(false);
  const [videosReportaje, setVideosReportaje] = useState([]);
  const [documentosPropiedad, setDocumentosPropiedad] = useState([]);
  const [lightboxFotoIdx, setLightboxFotoIdx] = useState(null);
  const [lightboxFotoSoloConsulta, setLightboxFotoSoloConsulta] = useState(false);
  /** 'pantalla' = una foto a pantalla completa; 'parrilla' = 3 fotos por fila */
  const [lightboxVista, setLightboxVista] = useState('pantalla');
  /** ids seleccionados en parrilla para acciones en lote */
  const [lightboxParrillaSelIds, setLightboxParrillaSelIds] = useState([]);
  /** 'todas' | 'marco' | 'seleccionadas' — modo del visor */
  const [lightboxModoFotos, setLightboxModoFotos] = useState('todas');
  /** Panel metadatos/EXIF de una foto (lazy al pulsar «i»). */
  const [fotoMetaPanel, setFotoMetaPanel] = useState(null); // { fotoId, loading, error, data }
  const [insertarMarcoOpen, setInsertarMarcoOpen] = useState(false);
  const [insertarPreviewIdx, setInsertarPreviewIdx] = useState(null);
  /** Refs de las miniaturas del panel "Insertar en selección Marco", para resaltar/ubicar la que se ve en grande. */
  const insertarThumbRefsRef = useRef({});
  const [prioridadSeleccion, setPrioridadSeleccion] = useState('marco');
  const [seleccionMarcoEstado, setSeleccionMarcoEstado] = useState(null);
  const [visorVideoUrl, setVisorVideoUrl] = useState(null);
  const [visorVideoError, setVisorVideoError] = useState(null);
  const [capFresh, setCapFresh] = useState(null);
  const [guardandoSecretaria, setGuardandoSecretaria] = useState(false);
  const [guardandoDirector, setGuardandoDirector] = useState(false);
  const [guardandoCompletar, setGuardandoCompletar] = useState(false);
  const [msgConforme, setMsgConforme] = useState('');
  const fotosSeccionRef = useRef(null);
  const [fotosSeccionVisible, setFotosSeccionVisible] = useState(false);
  const [generandoDesc, setGenerandoDesc] = useState('');
  const [docPreview, setDocPreview] = useState(null);
  const [fichaCaptacionGenerando, setFichaCaptacionGenerando] = useState(false);
  const [fichaCaptacionError, setFichaCaptacionError] = useState('');
  const [certCatastroGenerando, setCertCatastroGenerando] = useState(false);
  const [certCatastroError, setCertCatastroError] = useState('');
  const [modalDesviacionCatastro, setModalDesviacionCatastro] = useState(null);
  const [desviacionCatastroDismissed, setDesviacionCatastroDismissed] = useState(false);

  const abrirLightboxFoto = (idx, soloConsulta = false, modo = 'todas') => {
    setLightboxFotoSoloConsulta(soloConsulta);
    setLightboxModoFotos(modo);
    setLightboxVista('pantalla');
    setLightboxParrillaSelIds([]);
    setLightboxFotoIdx(idx);
  };

  const cerrarLightboxFoto = () => {
    setLightboxFotoIdx(null);
    setLightboxFotoSoloConsulta(false);
    setLightboxVista('pantalla');
    setLightboxParrillaSelIds([]);
    setInsertarMarcoOpen(false);
    setInsertarPreviewIdx(null);
  };

  useEffect(() => {
    if (!isOpen) {
      setFotosSeccionVisible(false);
      return undefined;
    }
    const t = window.setTimeout(() => setFotosSeccionVisible(true), 350);
    const el = fotosSeccionRef.current;
    if (el && typeof IntersectionObserver !== 'undefined') {
      const obs = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) setFotosSeccionVisible(true);
        },
        { rootMargin: '160px' },
      );
      obs.observe(el);
      return () => {
        clearTimeout(t);
        obs.disconnect();
      };
    }
    return () => clearTimeout(t);
  }, [isOpen, captacion?.id]);

  useEffect(() => {
    if (isOpen && captacion) {
      // No reutilizar textos del listado/captación anterior (el listado omite o iba recortado).
      setValoresExtras({});
      setCapFresh(null);
      setCamposObligatorios([]);
      setCamposCompletos([]);
      cargarCamposObligatorios();
      cargarVideosReportaje();
      cargarDocumentosPropiedad();
      setCalificacion(parseCalificacionCaptacion(captacion));
      setError('');
      setValidado(false);
      setAccionArchivo(null);
      setMotivoArchivo('');
      setMsgConforme('');
      cerrarLightboxFoto();
      setVisorVideoUrl(null);
      setGenerandoDesc('');
      setPrioridadSeleccion(parseExtrasCap(captacion).prioridad_seleccion === 'todas' ? 'todas' : 'marco');
      setSeleccionMarcoEstado(parseExtrasCap(captacion).seleccion_marco_estado || null);
      setFichaCaptacionError('');
      setFichaCaptacionGenerando(false);
      setCertCatastroError('');
      setCertCatastroGenerando(false);
      setModalDesviacionCatastro(null);
      setDesviacionCatastroDismissed(false);
    }
  }, [isOpen, captacion?.id]);

  useEffect(() => {
    if (!isOpen || !captacion?.id) return undefined;
    if (origenMultimediaCaptacion(capFresh || captacion) !== 'movil') return undefined;
    let cancelled = false;
    (async () => {
      const docs = await cargarDocumentosPropiedad();
      if (cancelled) return;
      const tieneFicha = (docs || []).some((d) => d.tipo_codigo === 'ficha_captacion');
      if (!tieneFicha) await asegurarFichaCaptacionMovil();
    })();
    return () => { cancelled = true; };
  }, [isOpen, captacion?.id]);

  // Ficha gráfica catastral: solo si falta el PDF real y el agente no la omitió.
  useEffect(() => {
    if (!isOpen || !captacion?.id) return undefined;
    const etapa = String((capFresh || captacion)?.etapa || '');
    if (etapa !== 'revisar_cap' && etapa !== 'captacion') return undefined;
    let cancelled = false;
    (async () => {
      const docs = await cargarDocumentosPropiedad();
      if (cancelled) return;
      const extras = parseExtrasCap(capFresh || captacion);
      if (extras.catastro_cert_omitido_usuario === true) {
        setCertCatastroError('');
        return;
      }
      const tienePdf = (docs || []).some((d) => d.tipo_codigo === 'cert_grafica_catastral');
      if (extras.catastro_desviaciones?.hayDesviacion && !desviacionCatastroDismissed) {
        setModalDesviacionCatastro(extras.catastro_desviaciones);
      }
      if (tienePdf) {
        setCertCatastroError('');
        return;
      }
      // Ya se generó antes y no hay PDF → se borró (aunque sea antes del flag omitido).
      // No auto-recrear; el agente usa «Generar ficha catastral» si quiere.
      if (extras.catastro_cert_generada_at || extras.catastro_cert_doc_id || extras.catastro_cert_url) {
        setCertCatastroError('');
        return;
      }
      // Primera vez (nunca generada): crear automáticamente.
      if (!extras.catastro_cert_generando) {
        await asegurarCertGraficaCatastral({ forzar: false });
      } else if (extras.catastro_cert_error) {
        setCertCatastroError(String(extras.catastro_cert_error));
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, captacion?.id]);

  // Si falla la generación inicial, reintentar solo en segundo plano (sin botón).
  useEffect(() => {
    if (!isOpen || !captacion?.id) return undefined;
    if (origenMultimediaCaptacion(capFresh || captacion) !== 'movil') return undefined;
    if (!fichaCaptacionError) return undefined;
    if (documentosPropiedad.some((d) => d.tipo_codigo === 'ficha_captacion')) return undefined;
    if (fichaCaptacionGenerando) return undefined;
    const t = window.setTimeout(() => {
      asegurarFichaCaptacionMovil();
    }, 4000);
    return () => window.clearTimeout(t);
  }, [isOpen, captacion?.id, fichaCaptacionError, fichaCaptacionGenerando, documentosPropiedad]);

  // Cargar fotos al abrir aunque la sección de secretaría aún no esté en viewport
  // (escritorio antes ocultaba el ref → IntersectionObserver no disparaba; el director
  // quedaba con lista vacía o solo tras el timeout). Ampliar: siempre al abrir.
  useEffect(() => {
    if (isOpen && captacion) {
      cargarFotosReportaje();
    }
  }, [isOpen, captacion?.id, fotosSeccionVisible]);

  // Poll estado selección Marco mientras no es terminal (móvil y escritorio).
  // Antes solo móvil → Hira/escritorio se quedaba en «Pendiente» sin refrescar.
  useEffect(() => {
    if (!isOpen || !captacion?.id) return undefined;
    const etapa = String((capFresh || captacion)?.etapa || '');
    if (etapa && etapa !== 'revisar_cap' && etapa !== 'captacion') return undefined;
    const estado = seleccionMarcoEstado;
    if (estado === 'ok' || estado === 'error') return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await api.get(`/captaciones/${captacion.id}`);
        const row = res.data?.data || res.data?.captacion || res.data;
        if (cancelled || !row) return;
        const ex = parseExtrasCap(row);
        setSeleccionMarcoEstado(ex.seleccion_marco_estado || null);
        if (ex.prioridad_seleccion === 'todas' || ex.prioridad_seleccion === 'marco') {
          setPrioridadSeleccion(ex.prioridad_seleccion);
        }
        setCapFresh((prev) => ({ ...(prev || captacion), ...row, datos_extras: ex }));
        if (ex.seleccion_marco_estado === 'ok' || ex.seleccion_marco_estado === 'error') {
          cargarFotosReportaje();
        }
      } catch { /* ignore */ }
    };
    tick();
    const id = window.setInterval(tick, 4000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- solo re-poll mientras estado no es terminal
  }, [isOpen, captacion?.id, seleccionMarcoEstado]);

  const cargarFotosReportaje = async () => {
    if (!captacion) return;
    try {
      const res = await api.get(`/captaciones/${captacion.id}/fotos-reportaje`);
      const fotos = (res.data.fotos || []).map((f) => ({
        ...mapFotoCaptacionDesdeApi(f),
        origen: 'captacion',
        usar_web: f.usar_web !== false,
        en_seleccion_marco: f.en_seleccion_marco === true,
      }));
      setFotosReportaje(fotos);
      if (Object.prototype.hasOwnProperty.call(res.data || {}, 'seleccion_marco_estado')) {
        setSeleccionMarcoEstado(res.data.seleccion_marco_estado || null);
      }
      if (res.data?.prioridad_seleccion === 'todas' || res.data?.prioridad_seleccion === 'marco') {
        setPrioridadSeleccion(res.data.prioridad_seleccion);
      }
    } catch {
      setFotosReportaje([]);
    }
  };

  const cargarVideosReportaje = async () => {
    if (!captacion) return;
    try {
      const res = await api.get(`/captaciones/${captacion.id}/videos-reportaje`);
      setVideosReportaje(res.data.videos || []);
    } catch { setVideosReportaje([]); }
  };

  // Si algún vídeo aún no tiene poster, reconsultar mientras el backend regenera.
  // Dependencia estable por ids sin thumb (no reiniciar el poll en cada array nuevo).
  const videosSinThumbKey = videosReportaje
    .filter((v) => v?.id && !v.url_thumb)
    .map((v) => v.id)
    .join(',');
  useEffect(() => {
    if (!isOpen || !captacion?.id || !videosSinThumbKey) return undefined;
    let cancelled = false;
    let n = 0;
    const tick = () => {
      if (cancelled) return;
      n += 1;
      cargarVideosReportaje();
      if (n < 24) {
        timer = setTimeout(tick, 4000);
      }
    };
    let timer = setTimeout(tick, 2000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- poll mientras falten thumbs concretos
  }, [isOpen, captacion?.id, videosSinThumbKey]);

  // Parrilla: solo thumbs. Si faltan (móvil antiguo), el listado ya encola backfill;
  // reconsultar hasta que lleguen para no pintar placeholders eternos.
  const fotosSinThumbKey = fotosReportaje
    .filter((f) => f?.id && !fotoTieneThumbGrid(f))
    .map((f) => f.id)
    .join(',');
  useEffect(() => {
    if (!isOpen || !captacion?.id || !fotosSinThumbKey) return undefined;
    let cancelled = false;
    let n = 0;
    const tick = () => {
      if (cancelled) return;
      n += 1;
      cargarFotosReportaje();
      if (n < 20) timer = setTimeout(tick, 2500);
    };
    let timer = setTimeout(tick, 1500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- poll mientras falten thumbs de foto
  }, [isOpen, captacion?.id, fotosSinThumbKey]);

  const abrirMetadatosFoto = async (foto) => {
    if (!captacion?.id || !foto?.id) return;
    setFotoMetaPanel({ fotoId: foto.id, loading: true, error: null, data: null });
    try {
      const res = await api.get(`/captaciones/${captacion.id}/fotos-reportaje/${foto.id}/metadata`);
      setFotoMetaPanel({ fotoId: foto.id, loading: false, error: null, data: res.data });
    } catch (e) {
      setFotoMetaPanel({
        fotoId: foto.id,
        loading: false,
        error: e?.response?.data?.error || e.message || 'No se pudieron leer los metadatos',
        data: null,
      });
    }
  };

  /** PDFs en la ficha: vienen de captación (borrador) y/o de la propiedad ya creada — el modal debe mostrar ambos. */
  const cargarDocumentosPropiedad = async () => {
    if (!captacion?.id) {
      setDocumentosPropiedad([]);
      return;
    }
    const desdeCap = [];
    const desdeProp = [];
    try {
      const resCap = await api.get(`/captaciones/${captacion.id}/documentos`);
      const dc = resCap.data?.data ?? resCap.data;
      if (Array.isArray(dc)) desdeCap.push(...dc);
    } catch { /* ignore */ }
    if (captacion.propiedad_id) {
      try {
        const resP = await api.get(`/propiedades/${captacion.propiedad_id}/documentos`);
        const dp = resP.data?.data ?? resP.data;
        if (Array.isArray(dp)) desdeProp.push(...dp);
      } catch { /* ignore */ }
    }
    const seen = new Set();
    const merged = [];
    let fichaYa = false;
    // Captación primero: su ficha_captacion es la regenerada; la de propiedad puede quedar obsoleta.
    for (const d of [...desdeCap, ...desdeProp]) {
      if (esDocFichaCaptacion(d)) {
        if (fichaYa) continue;
        fichaYa = true;
      }
      const u = String(d?.url || "").trim().toLowerCase();
      const key = u || String(d?.id || "");
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(d);
    }
    setDocumentosPropiedad(merged);
    return merged;
  };

  const asegurarFichaCaptacionMovil = async ({ forzar = false } = {}) => {
    if (!captacion?.id) return;
    if (origenMultimediaCaptacion(capFresh || captacion) !== 'movil') return;
    if (fichaCaptacionGenerando) return; // no stampede concurrente → EAGAIN Chromium
    setFichaCaptacionGenerando(true);
    setFichaCaptacionError('');
    try {
      const res = await api.post(`/captaciones/${captacion.id}/ficha-captacion/generar`, forzar ? { forzar: true } : {});
      const data = res.data || {};
      if (!data.ok) {
        setFichaCaptacionError(data.error || data.motivo || 'No se pudo generar la ficha de captación');
        return;
      }
      setFichaCaptacionError('');
      await cargarDocumentosPropiedad();
      await cargarCamposObligatorios(true);
    } catch (e) {
      setFichaCaptacionError(e.response?.data?.error || e.message || 'Error generando ficha de captación');
    } finally {
      setFichaCaptacionGenerando(false);
    }
  };

  const asegurarCertGraficaCatastral = async ({ forzar = false } = {}) => {
    if (!captacion?.id) return;
    setCertCatastroGenerando(true);
    setCertCatastroError('');
    try {
      const res = await api.post(
        `/captaciones/${captacion.id}/cert-grafica-catastral/generar`,
        forzar ? { forzar: true } : {},
      );
      const data = res.data || {};
      if (data.error || data.motivo === 'sin_rc') {
        setCertCatastroError(data.error || data.motivo || 'No se pudo obtener la ficha catastral');
      } else {
        setCertCatastroError('');
      }
      await cargarDocumentosPropiedad();
      await cargarCamposObligatorios(true);
      // Refrescar extras (doc_id / error / desviaciones) tras generar
      try {
        const capRes = await api.get(`/captaciones/${captacion.id}`);
        const row = capRes.data?.data || capRes.data?.captacion || capRes.data;
        if (row) {
          const ex = parseExtrasCap(row);
          setCapFresh((prev) => ({ ...(prev || captacion), ...row, datos_extras: ex }));
          if (ex.catastro_desviaciones?.hayDesviacion && !desviacionCatastroDismissed) {
            setModalDesviacionCatastro(ex.catastro_desviaciones);
          }
          if (ex.catastro_cert_error && !ex.catastro_cert_doc_id) {
            setCertCatastroError(ex.catastro_cert_error);
          }
        }
      } catch { /* noop */ }
      const desv = data.desviaciones
        || parseExtrasCap(capFresh || captacion).catastro_desviaciones;
      if (desv?.hayDesviacion && !desviacionCatastroDismissed) {
        setModalDesviacionCatastro(desv);
      }
    } catch (e) {
      setCertCatastroError(e.response?.data?.error || e.message || 'Error cert. gráfica catastral');
    } finally {
      setCertCatastroGenerando(false);
    }
  };

  const handleSubirFotosReportaje = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !captacion) return;
    e.target.value = '';

    const previews = await Promise.all(
      files.map(async (f, i) => {
        let thumb = null;
        try {
          thumb = await crearMiniaturaDesdeFile(f);
        } catch {
          thumb = URL.createObjectURL(f);
        }
        return {
          id: `temp-${Date.now()}-${i}`,
          url: URL.createObjectURL(f),
          preview: thumb,
          thumbPreview: thumb,
          esLocal: true,
        };
      }),
    );
    setFotosReportaje(prev => [...prev, ...previews]);
    setSubiendoFotos(true);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('fotos', f));
      const res = await postCaptacionMultipart(`/captaciones/${captacion.id}/fotos-reportaje`, formData);
      const fotosSubidas = (res.data.fotos || []).map((f) => ({
        ...mapFotoCaptacionDesdeApi(f),
        esExistente: true,
      }));
      const idsNuevos = new Set(fotosSubidas.map((f) => f.id).filter(Boolean));
      setFotosReportaje((prev) => [
        ...prev.filter((f) => !f.esLocal && !idsNuevos.has(f.id)),
        ...fotosSubidas,
      ]);
      // Nuevas fotos → Marco reevalúa (incremental) frente a la selección actual
      if (esOrigenMovil || origenMultimediaCaptacion(capFresh || captacion) === 'movil') {
        setSeleccionMarcoEstado((prev) => (prev === 'ok' ? 'pendiente' : (prev || 'pendiente')));
      }
      await cargarFotosReportaje();
      cargarCamposObligatorios(true);
    } catch (err) {
      const detalle = err?.response?.data?.error || err?.message || '';
      setError(detalle ? `Error subiendo fotos: ${detalle}` : 'Error subiendo fotos');
      setFotosReportaje(prev => prev.filter(f => !f.esLocal));
    } finally {
      setSubiendoFotos(false);
    }
  };

  const guardarCuracionFoto = async (fotoId, patch) => {
    if (!captacion?.id || !fotoId) return false;
    setFotosReportaje((prev) => prev.map((f) => (f.id === fotoId ? { ...f, ...patch } : f)));
    try {
      await api.patch(`/captaciones/${captacion.id}/fotos-reportaje/${fotoId}/curacion`, { id: fotoId, ...patch });
    } catch {
      setError('Error guardando curación de foto');
      cargarFotosReportaje();
    }
    return false;
  };

  /** Gira 90° horario (-90 = antihorario) y persiste; secretaría corrige fallos de Marco. */
  const rotarFotoReportaje = async (foto, grados = 90) => {
    if (!captacion?.id || !foto?.id) return;
    try {
      const { data } = await api.post(
        `/captaciones/${captacion.id}/fotos-reportaje/${foto.id}/rotar`,
        { grados },
      );
      const actualizada = data?.foto;
      if (!actualizada?.url) {
        await cargarFotosReportaje();
        return;
      }
      const bust = `t=${Date.now()}`;
      const withBust = (u) => {
        if (!u) return u;
        const s = String(u);
        return s.includes('?') ? `${s}&${bust}` : `${s}?${bust}`;
      };
      const url = withBust(actualizada.url);
      const urlThumb = withBust(actualizada.url_thumb || actualizada.url);
      setFotosReportaje((prev) => prev.map((f) => (
        f.id === foto.id
          ? {
            ...f,
            ...actualizada,
            url,
            url_thumb: urlThumb,
            // Parrilla usa thumbPreview/preview (mapFotoCaptacionDesdeApi): hay que refrescarlos
            thumbPreview: urlThumb,
            preview: urlThumb,
            url_display: url,
          }
          : f
      )));
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Error girando la foto');
    }
  };

  const rotarVariasFotosReportaje = async (ids, grados = 90) => {
    const lista = (ids || []).filter(Boolean);
    if (!lista.length) return;
    for (const id of lista) {
      const foto = fotosReportaje.find((f) => f.id === id);
      if (foto) await rotarFotoReportaje(foto, grados);
    }
    setLightboxParrillaSelIds([]);
  };

  const toggleUsarWebFoto = (foto) => {
    const next = foto.usar_web === false;
    guardarCuracionFoto(foto.id, { usar_web: next });
  };

  const toggleMarcoFoto = (foto) => {
    const next = foto.marco_autoenhance === false;
    guardarCuracionFoto(foto.id, { marco_autoenhance: next });
  };

  /** Activa/desactiva edición (marco_autoenhance) en todas las fotos de la selección Marco. */
  const setEdicionTodasSeleccionMarco = (activar) => {
    if (!captacion?.id) return;
    const ids = fotosReportaje
      .filter((f) => f.en_seleccion_marco === true && f.id)
      .map((f) => f.id);
    if (!ids.length) return;
    setFotosReportaje((prev) => prev.map((f) => (
      ids.includes(f.id) ? { ...f, marco_autoenhance: activar } : f
    )));
    api.put(`/captaciones/${captacion.id}/fotos-reportaje/marco-autoenhance`, {
      flags: ids.map((id) => ({ id, marco_autoenhance: activar })),
    }).catch(() => {
      setError('Error guardando edición de fotos');
      cargarFotosReportaje();
    });
  };

  const quitarDeSeleccionMarco = async (foto) => {
    if (!foto?.id) return;
    await guardarCuracionFoto(foto.id, { en_seleccion_marco: false, usar_web: false });
  };

  /** Quita varias fotos de la selección Marco (optimistic + patches en paralelo). */
  const quitarVariasDeSeleccionMarco = async (ids) => {
    const uniq = [...new Set((ids || []).filter(Boolean))];
    if (!captacion?.id || !uniq.length) return;
    const idSet = new Set(uniq);
    setFotosReportaje((prev) => prev.map((f) => (
      idSet.has(f.id) ? { ...f, en_seleccion_marco: false, usar_web: false } : f
    )));
    setLightboxParrillaSelIds([]);
    try {
      for (const id of uniq) {
        await api.patch(`/captaciones/${captacion.id}/fotos-reportaje/${id}/curacion`, {
          id, en_seleccion_marco: false, usar_web: false,
        });
      }
      await cargarFotosReportaje();
      await cargarCamposObligatorios(true);
    } catch {
      setError('Error quitando fotos de la selección');
      cargarFotosReportaje();
    }
  };

  /** Excluye varias fotos de Web (usar_web: false). */
  const excluirVariasDeWeb = async (ids) => {
    const uniq = [...new Set((ids || []).filter(Boolean))];
    if (!captacion?.id || !uniq.length) return;
    const idSet = new Set(uniq);
    setFotosReportaje((prev) => prev.map((f) => (
      idSet.has(f.id) ? { ...f, usar_web: false } : f
    )));
    setLightboxParrillaSelIds([]);
    try {
      for (const id of uniq) {
        await api.patch(`/captaciones/${captacion.id}/fotos-reportaje/${id}/curacion`, {
          id, usar_web: false,
        });
      }
      await cargarFotosReportaje();
    } catch {
      setError('Error excluyendo fotos de Web');
      cargarFotosReportaje();
    }
  };

  /** Activa/desactiva edición (marco_autoenhance) en un conjunto arbitrario de fotos (selección en lote). */
  const setEdicionVariasFotos = (ids, activar) => {
    const uniq = [...new Set((ids || []).filter(Boolean))];
    if (!captacion?.id || !uniq.length) return;
    const idSet = new Set(uniq);
    setFotosReportaje((prev) => prev.map((f) => (
      idSet.has(f.id) ? { ...f, marco_autoenhance: activar } : f
    )));
    api.put(`/captaciones/${captacion.id}/fotos-reportaje/marco-autoenhance`, {
      flags: uniq.map((id) => ({ id, marco_autoenhance: activar })),
    }).catch(() => {
      setError('Error guardando edición de fotos');
      cargarFotosReportaje();
    });
  };

  /** Elimina varias fotos definitivamente (borrado real, no solo exclusión de la selección). */
  const eliminarVariasFotosReportaje = async (ids) => {
    const uniq = [...new Set((ids || []).filter(Boolean))];
    if (!uniq.length) return;
    if (!window.confirm(`¿Eliminar ${uniq.length} foto${uniq.length === 1 ? '' : 's'} definitivamente? Esta acción no se puede deshacer.`)) return;
    const idSet = new Set(uniq);
    setFotosReportaje((prev) => prev.filter((f) => !idSet.has(f.id)));
    setLightboxParrillaSelIds([]);
    try {
      for (const id of uniq) {
        await api.delete(`/captaciones/fotos-reportaje/${id}`);
      }
      await cargarFotosReportaje();
      await cargarCamposObligatorios(true);
    } catch {
      setError('Error eliminando fotos');
      cargarFotosReportaje();
    }
  };

  const toggleLightboxParrillaSel = (fotoId) => {
    if (!fotoId) return;
    setLightboxParrillaSelIds((prev) => (
      prev.includes(fotoId) ? prev.filter((id) => id !== fotoId) : [...prev, fotoId]
    ));
  };

  const insertarEnSeleccionMarco = (foto) => {
    if (!foto?.id) return;
    guardarCuracionFoto(foto.id, { en_seleccion_marco: true, usar_web: true });
  };

  const guardarPrioridadSeleccion = async (prioridad) => {
    if (!captacion?.id) return;
    const next = prioridad === 'todas' ? 'todas' : 'marco';
    setPrioridadSeleccion(next);
    try {
      const res = await api.patch(`/captaciones/${captacion.id}/seleccion-prioridad`, { prioridad: next });
      const extras = res.data?.datos_extras;
      if (extras) {
        setCapFresh((prev) => {
          const base = prev || captacion;
          return { ...base, datos_extras: extras };
        });
      }
    } catch {
      setError('Error guardando prioridad de selección');
    }
  };

  const eliminarFotoReportaje = async (fotoId) => {
    setFotosReportaje(prev => prev.filter(f => f.id !== fotoId));
    try {
      await api.delete(`/captaciones/fotos-reportaje/${fotoId}`);
      await cargarFotosReportaje();
      await cargarCamposObligatorios(true);
    } catch { setError('Error eliminando foto'); }
  };

  const moverFotoReportaje = (fromIdx, toIdx) => {
    if (fromIdx === toIdx || fromIdx == null || toIdx == null) return;
    setFotosReportaje(prev => {
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      const ids = next.filter(f => f.id).map(f => f.id);
      api.put(`/captaciones/${captacion.id}/fotos-reportaje/orden`, { orden: ids }).catch(() => {});
      return next;
    });
  };

  /**
   * Reordena la lista visible en la parrilla del lightbox y persiste orden.
   * En modo Marco/seleccionadas: las fotos de la lista quedan al frente (portada = #1).
   */
  const moverFotoEnParrillaLightbox = (fromIdx, toIdx) => {
    if (fromIdx === toIdx || fromIdx == null || toIdx == null) return;
    if (lightboxFotoSoloConsulta) return;

    setFotosReportaje((prev) => {
      let lista;
      if (lightboxModoFotos === 'marco') {
        lista = prev.filter((f) => f.en_seleccion_marco === true);
      } else if (lightboxModoFotos === 'seleccionadas') {
        lista = prioridadSeleccion === 'marco'
          ? prev.filter((f) => f.en_seleccion_marco === true)
          : prev.filter((f) => f.usar_web !== false);
      } else {
        lista = [...prev];
      }
      if (fromIdx < 0 || toIdx < 0 || fromIdx >= lista.length || toIdx >= lista.length) return prev;

      const ordered = [...lista];
      const [moved] = ordered.splice(fromIdx, 1);
      ordered.splice(toIdx, 0, moved);

      let next;
      if (lightboxModoFotos === 'todas') {
        next = ordered;
      } else {
        const idSel = new Set(ordered.map((f) => f.id).filter(Boolean));
        const resto = prev.filter((f) => !idSel.has(f.id));
        next = [...ordered, ...resto];
      }

      const ids = next.filter((f) => f.id).map((f) => f.id);
      if (captacion?.id && ids.length) {
        api.put(`/captaciones/${captacion.id}/fotos-reportaje/orden`, { orden: ids }).catch(() => {});
      }
      return next;
    });
    setLightboxFotoIdx(toIdx);
  };

  useEffect(() => {
    if (!visorVideoUrl || visorVideoUrl === '__pending__') return;
    const el = visorVideoRef.current;
    if (!el) return;
    el.muted = false;
    const tid = window.setTimeout(() => {
      el.play().catch(() => {
        el.muted = true;
        el.play().catch(() => {});
      });
    }, 0);
    return () => window.clearTimeout(tid);
  }, [visorVideoUrl]);

  useEffect(() => {
    if (!isOpen) return;
    api.get('/agentes')
      .then((res) => setAgentes(res.data?.data || []))
      .catch(() => setAgentes([]));
    api.get('/auxiliares/tipo-via')
      .then((res) => setTiposVia(res.data?.data || []))
      .catch(() => setTiposVia([]));
    api.get('/auxiliares/tipo-inmueble')
      .then((res) => setTiposInmueble(res.data?.data || []))
      .catch(() => setTiposInmueble([]));
  }, [isOpen]);

  const provinciaIdActual = (() => {
    const raw = valoresExtras.provincia_id ?? valoresExtras.provincia ?? '';
    if (!raw) return '';
    if (/^\d{1,2}$/.test(String(raw))) return String(raw).padStart(2, '0');
    const prov = PROVINCIAS_ES.find((p) => p.nombre.toLowerCase() === String(raw).toLowerCase());
    return prov?.id || '';
  })();

  useEffect(() => {
    const hayPoblacion = camposObligatorios.some((c) => c.campo_key === 'poblacion' || c.campo_key === 'poblacion_id');
    if (!isOpen || !hayPoblacion || !provinciaIdActual) {
      setPoblaciones([]);
      return;
    }
    api.get(`/auxiliares/poblaciones/${provinciaIdActual}`)
      .then((res) => setPoblaciones(res.data?.data || []))
      .catch(() => setPoblaciones([]));
  }, [isOpen, provinciaIdActual, camposObligatorios]);

  const auxCampoKeys = useMemo(
    () => [...camposObligatorios, ...camposCompletos].map((c) => c.campo_key).filter(Boolean),
    [camposObligatorios, camposCompletos]
  );
  const auxLists = useAuxListsForCampos(isOpen, auxCampoKeys);

  const cargarCamposObligatorios = async (preserveValues = false) => {
    try {
      const [camposRes, capRes] = await Promise.all([
        api.get(`/campos-obligatorios/verificar-captacion/${captacion.id}?rol=secretaria,director`),
        api.get(`/captaciones/${captacion.id}`),
      ]);
      const freshCap = capRes.data?.data || capRes.data || captacion;
      setCapFresh(freshCap);
      setCalificacion(parseCalificacionCaptacion(freshCap));
      const extras = parseExtrasCap(freshCap);
      const rawPv = freshCap.precio_venta != null && freshCap.precio_venta !== ''
        ? freshCap.precio_venta
        : (extras.precio_venta ?? '');
      const nPv = parsePrecioVentaEntero(rawPv);
      const pvFromServer = Number.isFinite(nPv) && nPv >= 0 ? formatPrecioVentaEntero(nPv) : '';
      // Ampliar: con preserveValues no pisar el borrador local de Precio venta.
      // El poll de textos web / ficha (cada 2,5 s) llama cargarCamposObligatorios(true)
      // y antes vaciaba precioVentaStr porque en BD aún no estaba guardado → al
      // pasar al bloque de honorarios el precio "desaparecía".
      if (!preserveValues) {
        setPrecioVentaStr(pvFromServer);
      } else {
        setPrecioVentaStr((prev) => {
          if (String(prev || '').trim()) return prev;
          return pvFromServer;
        });
      }

      const dedupeAndFilter = (arr = []) => {
        const map = new Map();
        arr.forEach((c) => {
          if (!c?.campo_key) return;
          if (isCalificacionKey(c.campo_key)) return;
          if (!map.has(c.campo_key)) map.set(c.campo_key, c);
        });
        return Array.from(map.values());
      };
      const faltantes = dedupeAndFilter(camposRes.data.camposFaltantes || []);
      const completos = dedupeAndFilter(camposRes.data.camposCompletos || []);
      setCamposObligatorios(faltantes);
      setCamposCompletos(completos);

      const mergeHonDesdeExtras = (base) => {
        const out = { ...base };
        HON_MERGE.forEach((k) => {
          const ev = extras[k];
          if (ev === undefined || ev === null || ev === '') return;
          const cur = out[k];
          if (cur === undefined || cur === null || cur === '') out[k] = ev;
        });
        if (!out.titulo && extras.web_titulo) {
          const t = tituloCrmUsable(extras.web_titulo, extras);
          if (t) out.titulo = t;
        }
        return out;
      };
      if (!preserveValues) {
        const vals = { ...extras };
        // Ampliar: columnas nativas calle/número → campos de ficha (nombre_via/numero)
        // si el móvil no los dejó bien en extras (tarjeta sí los muestra).
        if (!String(vals.nombre_via || '').trim() && (freshCap.calle || extras.calle)) {
          vals.nombre_via = freshCap.calle || extras.nombre_via || extras.calle || '';
        }
        if (!String(vals.numero || '').trim() && freshCap.numero) {
          vals.numero = freshCap.numero;
        }
        if (!String(vals.poblacion_nombre || '').trim()) {
          const pobRaw = extras.poblacion_nombre || extras.poblacion || freshCap.poblacion || '';
          if (pobRaw && !/^\d{4,5}$/.test(String(pobRaw).trim())) vals.poblacion_nombre = pobRaw;
        }
        if (esParcelaBasuraTipo(vals.parcela)) vals.parcela = '';
        const rustica = normalizarDireccionRusticaCaptacion({
          nombre_via: vals.nombre_via || freshCap.calle,
          calle: freshCap.calle || vals.calle,
          numero: vals.numero || freshCap.numero,
          direccion: vals.direccion || extras.direccion,
          parcela: vals.parcela,
          poligono: vals.poligono,
        });
        if (rustica) Object.assign(vals, rustica);
        [...faltantes, ...completos].forEach((f) => {
          const k = f.campo_key;
          if (vals[k] === undefined || vals[k] === null || vals[k] === '') {
            if (getTipoCampo(k, f.tipo_campo) === 'boolean') {
              vals[k] = extras[k] === true || extras[k] === 'true' || extras[k] === 1;
            } else if (k === 'disponible_desde' && !extras[k]) {
              vals[k] = hoyISO();
            } else {
              vals[k] = extras[k] ?? '';
            }
          }
        });
        // Reaplicar rústica tras el seed de faltantes: un `numero` vacío del parse
        // no debe rellenarse otra vez con "Polígono…, parcelas…" de extras.
        if (rustica) {
          Object.assign(vals, rustica);
          if (esParcelaBasuraTipo(vals.parcela) && rustica.parcela) vals.parcela = rustica.parcela;
          if (esParcelaBasuraTipo(vals.parcela)) vals.parcela = '';
        }
        SEED_EXTRAS_KEYS.forEach((k) => {
          if (extras[k] !== undefined && extras[k] !== null && extras[k] !== '') vals[k] = extras[k];
        });
        // No sembrar título Idealista/Fotocasa (calle) en el campo web CRM.
        const tituloLimpio = tituloCrmUsable(vals.web_titulo || vals.titulo, extras)
          || tituloCrmUsable(extras.web_titulo, extras)
          || tituloCrmUsable(extras.titulo, extras);
        if (esTituloDePortalScraping(vals.titulo, extras) || esTituloDePortalScraping(vals.web_titulo, extras)) {
          vals.titulo = tituloLimpio;
          vals.web_titulo = tituloLimpio;
        }
        if (normalizeVistasIdsFromExtras(extras).length) {
          Object.assign(vals, buildVistasExtrasPatch(normalizeVistasIdsFromExtras(extras)));
        }
        setValoresExtras(mergeHonDesdeExtras(vals));
      } else {
        setValoresExtras((prev) => {
          const merged = mergeHonDesdeExtras({ ...prev });
          // Ampliar: textos web del servidor solo si el local está vacío (IA en background).
          // No pisar ediciones manuales ya hechas en el modal.
          SEED_EXTRAS_KEYS.forEach((k) => {
            if (!CAMPOS_WEB_SECRETARIA.has(k)) return;
            const ev = extras[k];
            if (ev === undefined || ev === null || ev === '') return;
            const cur = merged[k];
            if (cur === undefined || cur === null || cur === '') {
              merged[k] = ev;
              return;
            }
            // Local tiene basura de portal y el servidor ya tiene título limpio → preferir servidor.
            if ((k === 'titulo' || k === 'web_titulo')
              && esTituloDePortalScraping(cur, extras)
              && tituloCrmUsable(ev, extras)) {
              merged[k] = ev;
            }
          });
          return merged;
        });
      }
    } catch (e) {
      console.error('Error verificando campos obligatorios:', e);
      // No vaciar faltantes: un fallo de red no debe hacer creer que ya está todo completo.
      setError('Error cargando campos obligatorios. Cierra y vuelve a abrir el modal.');
    }
  };

  const persistDescCaptacion = async (updates) => {
    if (!captacion?.id || !updates || !Object.keys(updates).length) return;
    setValoresExtras((prev) => ({ ...prev, ...updates }));
    try {
      // Persistir sin conforme: ya en Para Revisar no auto-avanza.
      await api.patch(`/captaciones/${captacion.id}/datos-extras`, {
        datos: updates,
        solo_borrador: true,
      });
    } catch {
      setError('Error guardando descripciones');
    }
  };

  const persistTextosWebSiCambiaron = async () => {
    if (!captacion?.id) return;
    // Solo comparar contra GET completo (capFresh). El `captacion` del pipeline puede ir sin desc.
    if (!capFresh) return;
    const extras = parseExtrasCap(capFresh);
    const local = valoresExtrasRef.current || {};
    const tituloLocal = String(local.titulo ?? local.web_titulo ?? '').trim();
    const tituloServer = String(extras.web_titulo || extras.titulo || '').trim();
    const descServer = String(extras.web_desc_larga_es || '');
    const updates = {};
    if (tituloLocal && tituloLocal !== tituloServer) {
      // No pisar título CRM limpio con título de portal (calle Idealista).
      const localEsPortal = esTituloDePortalScraping(tituloLocal, extras);
      const serverLimpio = tituloCrmUsable(tituloServer, extras);
      if (localEsPortal && serverLimpio) {
        // no-op: el servidor ya tiene «Piso en Orba»; el modal no debe reinyectar la calle.
      } else if (localEsPortal) {
        // no-op: no persistir portal como web_titulo.
      } else {
        updates.titulo = tituloLocal;
        updates.web_titulo = tituloLocal;
      }
    }
    if (Object.prototype.hasOwnProperty.call(local, 'web_desc_larga_es')) {
      const rawLocal = local.web_desc_larga_es;
      if (rawLocal !== undefined && rawLocal !== null && String(rawLocal) !== descServer) {
        const localStr = String(rawLocal);
        const localTrim = localStr.trim();
        // Nunca borrar ni acortar la desc del servidor con blur/listado incompleto.
        if (!localTrim && descServer.trim()) {
          // no-op
        } else {
          const esRecorteOVacio =
            !localTrim
            || (
              descServer.length > localStr.length
              && descServer.startsWith(localStr)
            );
          if (!esRecorteOVacio) {
            updates.web_desc_larga_es = localStr;
          }
        }
      }
    }
    if (!Object.keys(updates).length) return;
    try {
      await api.patch(`/captaciones/${captacion.id}/datos-extras`, {
        datos: updates,
        solo_borrador: true,
      });
      setCapFresh((prev) => {
        const base = prev || captacion;
        const ex = parseExtrasCap(base);
        return { ...base, datos_extras: { ...ex, ...updates } };
      });
    } catch {
      setError('Error guardando textos web');
    }
  };

  const generarDescripcionCap = async (tipo, { forzar = false } = {}) => {
    if (!captacion || generandoDesc) return;
    const cap = capFresh || captacion;
    const extras = parseExtrasCap(cap);
    const merged = { ...extras, ...valoresExtras };
    const key = tipo === 'corta' ? 'web_desc_corta_es' : 'web_desc_larga_es';
    if (!forzar && String(merged[key] || '').trim()) return;

    const form = buildFormCaptacionParaIA(cap, extras, valoresExtras, tiposInmueble);
    const superficies = Array.isArray(extras.superficies_ficha) ? extras.superficies_ficha : [];
    const ctx = buildContextoPropiedadIA(form, auxiliaresParaIACaptacion(auxLists, tiposInmueble), superficies);
    if (!ctx || ctx.length < 20) {
      setError('Faltan datos de la propiedad para generar la descripción');
      return;
    }

    setGenerandoDesc(tipo);
    setError('');
    try {
      const auxIA = auxiliaresParaIACaptacion(auxLists, tiposInmueble);
      const nombresProhibidos = [];
      const urb = (auxIA.urbanizacion || []).find((x) => String(x.id) === String(form.urbanizacion_id));
      const zona = (auxIA.zona || []).find((x) => String(x.id) === String(form.zona_id));
      if (urb?.nombre) nombresProhibidos.push(urb.nombre);
      if (zona?.nombre) nombresProhibidos.push(zona.nombre);
      const res = await api.post('/ia/descripcion', {
        contexto: ctx,
        tipo,
        idioma: 'es',
        nombresProhibidos,
        poblacionPermitida: form.poblacion_nombre || form.poblacion || null,
        m2_declarados_escritura: merged.m2_declarados_escritura,
        m2_se_pueden_declarar: merged.m2_se_pueden_declarar,
        m2_declaracion_quien_paga: merged.m2_declaracion_quien_paga,
      });
      const texto = res.data?.texto || '';
      if (!texto.trim()) return;
      await persistDescCaptacion({ [key]: texto });
    } catch (err) {
      const st = err?.response?.status;
      const msg = st === 502 || st === 503 || st === 504
        ? 'El servidor no respondió a tiempo. Vuelve a intentarlo.'
        : (err.response?.data?.error || err.message);
      setError(`Error al generar descripción: ${msg}`);
    } finally {
      setGenerandoDesc('');
    }
  };

  useEffect(() => {
    if (!isOpen || !captacion?.id) return undefined;
    const extrasFresh = capFresh ? parseExtrasCap(capFresh) : {};
    const merged = { ...extrasFresh, ...valoresExtras };
    const tituloOk = !!tituloCaptacion(merged);
    const descOk = !!String(merged.web_desc_larga_es || '').trim();
    if (tituloOk && descOk) return undefined;
    let ticks = 0;
    const iv = window.setInterval(() => {
      ticks += 1;
      if (ticks > 20) {
        window.clearInterval(iv);
        return;
      }
      cargarCamposObligatorios(true);
    }, 2500);
    return () => window.clearInterval(iv);
  }, [
    isOpen,
    captacion?.id,
    valoresExtras.web_desc_larga_es,
    valoresExtras.titulo,
    valoresExtras.web_titulo,
  ]);

  useEffect(() => {
    if (!isOpen || !captacion?.id) return undefined;
    const extras = parseExtrasCap(capFresh || captacion);
    if (origenMultimediaCaptacion(capFresh || captacion) !== 'movil') return undefined;
    const generando = !!extras.ficha_captacion_generando || !!valoresExtras.ficha_captacion_generando;
    const tieneFicha = documentosPropiedad.some((d) => d.tipo_codigo === 'ficha_captacion');
    if (tieneFicha && !generando) return undefined;
    if (!tieneFicha && !generando) return undefined;
    let ticks = 0;
    let reintentoForzado = false;
    const iv = window.setInterval(() => {
      ticks += 1;
      // ~2.5 min de poll; a ~60s y ~150s forzar regeneración si sigue sin PDF
      // (cubre flag huérfano tras deploy / Chromium colgado).
      if (ticks === 24 || ticks === 60) {
        if (!documentosPropiedad.some((d) => d.tipo_codigo === 'ficha_captacion')) {
          reintentoForzado = true;
          asegurarFichaCaptacionMovil({ forzar: true });
        }
      }
      if (ticks > 72) {
        window.clearInterval(iv);
        if (!reintentoForzado && !documentosPropiedad.some((d) => d.tipo_codigo === 'ficha_captacion')) {
          asegurarFichaCaptacionMovil({ forzar: true });
        }
        return;
      }
      cargarDocumentosPropiedad();
      cargarCamposObligatorios(true);
    }, 2500);
    return () => window.clearInterval(iv);
  }, [isOpen, captacion?.id, capFresh, documentosPropiedad, valoresExtras.ficha_captacion_generando]);

  // Poll cert. catastral mientras genera
  useEffect(() => {
    if (!isOpen || !captacion?.id) return undefined;
    const extras = parseExtrasCap(capFresh || captacion);
    const generando = !!extras.catastro_cert_generando || certCatastroGenerando || !!valoresExtras.catastro_cert_generando;
    const tieneCert = documentosPropiedad.some((d) => d.tipo_codigo === 'cert_grafica_catastral')
      || !!extras.catastro_cert_doc_id;
    if (tieneCert && !generando) {
      if (extras.catastro_desviaciones?.hayDesviacion && !desviacionCatastroDismissed && !modalDesviacionCatastro) {
        setModalDesviacionCatastro(extras.catastro_desviaciones);
      }
      return undefined;
    }
    if (!generando && (extras.catastro_cert_error || extras.catastro_cert_m2_construidos != null)) {
      if (extras.catastro_desviaciones?.hayDesviacion && !desviacionCatastroDismissed && !modalDesviacionCatastro) {
        setModalDesviacionCatastro(extras.catastro_desviaciones);
      }
      if (extras.catastro_cert_error) setCertCatastroError(extras.catastro_cert_error);
      return undefined;
    }
    if (!generando) return undefined;
    let ticks = 0;
    const iv = window.setInterval(() => {
      ticks += 1;
      if (ticks > 36) {
        window.clearInterval(iv);
        return;
      }
      cargarDocumentosPropiedad();
      cargarCamposObligatorios(true);
    }, 2500);
    return () => window.clearInterval(iv);
  }, [
    isOpen,
    captacion?.id,
    capFresh,
    documentosPropiedad,
    certCatastroGenerando,
    valoresExtras.catastro_cert_generando,
    desviacionCatastroDismissed,
    modalDesviacionCatastro,
  ]);

  const handleGuardarExtras = async () => {
    setGuardandoExtras(true);
    setError('');
    setGuardadoExtrasOk(false);
    try {
      const extrasCap = (typeof captacion?.datos_extras === 'object' && captacion?.datos_extras !== null)
        ? captacion.datos_extras
        : (() => { try { return JSON.parse(captacion?.datos_extras || '{}'); } catch { return {}; } })();
      const exclusivaRaw = (valoresExtras.exclusiva !== undefined && valoresExtras.exclusiva !== null && valoresExtras.exclusiva !== '')
        ? valoresExtras.exclusiva
        : extrasCap.exclusiva;
      const exclusivaActiva = exclusivaRaw === true || exclusivaRaw === 'true' || exclusivaRaw === 1 || exclusivaRaw === '1';
      const payload = {};
      Object.entries(valoresExtras).forEach(([k, v]) => {
        if (CAMPOS_EXCLUSIVA_DEP.includes(k) && !exclusivaActiva) return;
        if (isMoneyField(k)) {
          const money = parseMoney(v);
          if (money !== null) payload[k] = money;
          return;
        }
        if (v !== '' && v !== null && v !== undefined) payload[k] = v;
      });
      const res = await api.patch(`/captaciones/${captacion.id}/datos-extras`, { datos: payload });
      if (await aplicarRetrocesoRespuesta(res)) return;
      setGuardadoExtrasOk(true);
      await cargarCamposObligatorios(true);
      // La ficha PDF se regenera sola en servidor; mostrar estado y refrescar el PDF.
      if (origenMultimediaCaptacion(capFresh || captacion) === 'movil') {
        setFichaCaptacionGenerando(true);
        setFichaCaptacionError('');
        window.setTimeout(() => { cargarDocumentosPropiedad(); cargarCamposObligatorios(true); }, 2800);
        window.setTimeout(() => {
          cargarDocumentosPropiedad();
          cargarCamposObligatorios(true);
          setFichaCaptacionGenerando(false);
        }, 5500);
      }
      setTimeout(() => setGuardadoExtrasOk(false), 2500);
    } catch (e) {
      setError(e.response?.data?.error || 'Error guardando campos');
    } finally {
      setGuardandoExtras(false);
    }
  };

  // Honorarios: mismas dos formas de cálculo que en ficha de propiedad
  const calcHonDesdeVentaYProp = () => {
    const pv = parsePrecioVentaEntero(precioVentaStr);
    const pp = parsePrecioVentaEntero(valoresExtras.precio_propietario);
    const ivaPct = parsePercentFlexible(valoresExtras.iva_porcentaje) || 21;
    if (!Number.isFinite(pv) || !Number.isFinite(pp) || pv <= pp) return;
    const dif = pv - pp;
    const honBase = dif / (1 + ivaPct / 100);
    const iva = dif - honBase;
    setValoresExtras(prev => ({
      ...prev,
      honorarios_porcentaje: roundEuro2((honBase / pv) * 100),
      honorarios_euros: roundEuro2(honBase),
      iva_porcentaje: roundEuro2(ivaPct),
      iva_euros: roundEuro2(iva),
      total_honorarios: roundEuro2(dif),
    }));
  };

  const calcHonDesdeVentaYPct = () => {
    const pv = parsePrecioVentaEntero(precioVentaStr);
    const pct = parsePercentFlexible(valoresExtras.honorarios_porcentaje);
    const ivaPct = parsePercentFlexible(valoresExtras.iva_porcentaje) || 21;
    if (!Number.isFinite(pv) || !Number.isFinite(pct)) return;
    const honBase = pv * (pct / 100);
    const iva = honBase * (ivaPct / 100);
    const total = honBase + iva;
    setValoresExtras(prev => ({
      ...prev,
      honorarios_euros: roundEuro2(honBase),
      iva_porcentaje: roundEuro2(ivaPct),
      iva_euros: roundEuro2(iva),
      // Incluidos %: propietario = venta − (hon + IVA), con céntimos
      precio_propietario: roundEuro2(pv - total),
      total_honorarios: roundEuro2(total),
    }));
  };

  const capActivaEarly = capFresh || captacion;
  const contadoresFotos = useMemo(() => {
    const total = fotosReportaje.length;
    const web = fotosReportaje.filter((f) => f.usar_web !== false).length;
    const marco = fotosReportaje.filter((f) => f.marco_autoenhance !== false).length;
    const seleccionMarco = fotosReportaje.filter((f) => f.en_seleccion_marco === true);
    const seleccionMarcoCount = seleccionMarco.length;
    const seleccionAEditar = seleccionMarco.filter((f) => f.marco_autoenhance !== false).length;
    return { total, web, marco, seleccionMarco: seleccionMarcoCount, seleccionAEditar };
  }, [fotosReportaje]);

  const fotosLightboxLista = useMemo(() => {
    if (lightboxModoFotos === 'marco') {
      return fotosReportaje.filter((f) => f.en_seleccion_marco === true);
    }
    if (lightboxModoFotos === 'seleccionadas') {
      // Misma lista que ve el director (prioridad Marco o web)
      if (prioridadSeleccion === 'marco') {
        const sel = fotosReportaje.filter((f) => f.en_seleccion_marco === true);
        if (sel.length) return sel;
      }
      return fotosReportaje.filter((f) => f.usar_web !== false);
    }
    return fotosReportaje;
  }, [fotosReportaje, lightboxModoFotos, prioridadSeleccion]);

  const fotosFueraSeleccionMarco = useMemo(
    () => fotosReportaje.filter((f) => f.en_seleccion_marco !== true),
    [fotosReportaje],
  );

  // Al abrir "Insertar en selección Marco" mostramos ya la primera foto en grande.
  // Si la lista se reduce (tras insertar una), reencuadramos el índice para seguir navegando sin saltar a las miniaturas.
  useEffect(() => {
    if (!insertarMarcoOpen) return;
    setInsertarPreviewIdx((idx) => {
      if (idx == null) return 0;
      if (idx >= fotosFueraSeleccionMarco.length) return Math.max(0, fotosFueraSeleccionMarco.length - 1);
      return idx;
    });
  }, [insertarMarcoOpen, fotosFueraSeleccionMarco.length]);

  // Al navegar con las flechas de la foto grande, desplaza la miniatura resaltada a la vista.
  useEffect(() => {
    if (!insertarMarcoOpen || insertarPreviewIdx == null) return;
    insertarThumbRefsRef.current[insertarPreviewIdx]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [insertarMarcoOpen, insertarPreviewIdx]);

  /** Fotos que verá el director (solo consulta): la selección priorizada. */
  const fotosDirectorConsulta = useMemo(() => {
    if (prioridadSeleccion === 'marco') {
      const sel = fotosReportaje.filter((f) => f.en_seleccion_marco === true);
      if (sel.length) return sel;
    }
    return fotosReportaje.filter((f) => f.usar_web !== false);
  }, [fotosReportaje, prioridadSeleccion]);

  // Si se elimina la foto actual de la selección, ajustar índice
  useEffect(() => {
    if (lightboxFotoIdx == null) return;
    if (!fotosLightboxLista.length) {
      setLightboxFotoIdx(null);
      return;
    }
    if (lightboxFotoIdx >= fotosLightboxLista.length) {
      setLightboxFotoIdx(fotosLightboxLista.length - 1);
    }
  }, [fotosLightboxLista, lightboxFotoIdx]);

  // Vista pantalla: flechas / Escape (la secretaria usa mucho el teclado al revisar)
  useEffect(() => {
    if (lightboxFotoIdx == null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cerrarLightboxFoto();
        return;
      }
      if (lightboxVista !== 'pantalla') return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setLightboxFotoIdx((i) => (i > 0 ? i - 1 : fotosLightboxLista.length - 1));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setLightboxFotoIdx((i) => (i < fotosLightboxLista.length - 1 ? i + 1 : 0));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxFotoIdx, lightboxVista, fotosLightboxLista.length]);

  const documentosClasificados = useMemo(() => {
    const encargos = [];
    const fichas = [];
    const certs = [];
    const otros = [];
    for (const d of documentosPropiedad) {
      if (esDocFichaCaptacion(d)) fichas.push(d);
      else if (esDocCertGraficaCatastral(d)) certs.push(d);
      else if (TIPOS_ENCARGO_DOC.has(d.tipo_codigo)) encargos.push(d);
      else otros.push(d);
    }
    return { encargos, fichas, certs, otros };
  }, [documentosPropiedad]);

  const solicitarCierreRevisar = useCallback(async () => {
    const guardando = guardandoSecretaria || guardandoDirector || guardandoExtras || guardandoCompletar;
    if (guardando) return;
    const cap = capFresh || captacion;
    const etapa = String(cap?.etapa || captacion?.etapa || '');
    if (cap?.id && (etapa === 'revisar_cap' || etapa === 'visita_cap')) {
      try {
        const res = await api.post(`/captaciones/${cap.id}/reconciliar-requisitos-revisar`);
        if (await aplicarRetrocesoRespuesta(res)) return;
      } catch (e) {
        console.warn('[ModalRevisar] reconciliar requisitos:', e?.message || e);
      }
    }
    onClose?.();
  }, [
    guardandoSecretaria,
    guardandoDirector,
    guardandoExtras,
    guardandoCompletar,
    capFresh,
    captacion,
    aplicarRetrocesoRespuesta,
    onClose,
  ]);

  if (!isOpen || !captacion) return null;
  const capActiva = capActivaEarly;
  const extrasCap = parseExtrasCap(capActiva);
  // Si el PDF está en extras pero aún no en la lista de docs, mostrarlo igual (secretaria y director).
  const fichaCaptacionDocsUi = (() => {
    if (documentosClasificados.fichas.length) return documentosClasificados.fichas;
    const url = absolutizarMediaUrl(extrasCap.ficha_captacion_url);
    if (!url && !extrasCap.ficha_captacion_doc_id) return [];
    return [{
      id: extrasCap.ficha_captacion_doc_id || 'ficha-extras',
      tipo_codigo: 'ficha_captacion',
      nombre_documento: 'Ficha de Captación',
      nombre_original: 'Ficha de Captación',
      url: url || '',
    }];
  })();
  const certCatastroDocsUi = (() => {
    // Solo documentos reales en BD. No inventar chip desde extras (tras borrar
    // el PDF reaparecía el fantasma de catastro_cert_url / doc_id).
    if (documentosClasificados.certs.length) return documentosClasificados.certs;
    return [];
  })();
  const docsDocumentacionUi = (() => {
    return [...documentosClasificados.certs, ...documentosClasificados.otros];
  })();
  const certCatastroOmitidoUi = extrasCap.catastro_cert_omitido_usuario === true;
  const refManual = refPipelineCard(capActiva, extrasCap);
  const esPrueba = esCaptacionPrueba(capActiva, extrasCap);
  const yaConformeSecretaria = !!capActiva.fecha_validacion_secretaria;
  const yaConformeDirector = !!capActiva.fecha_validacion_director;
  const pendienteCompletarValidacion = yaConformeSecretaria && yaConformeDirector && !capActiva.fecha_validacion;
  const ultimoErrorValidacion = extrasCap.ultimo_error_validacion || null;
  const labelConformeSecretaria = formatConformeEtiqueta(
    capActiva.fecha_validacion_secretaria,
    capActiva.validador_secretaria_nombre,
    'secretaría',
  );
  const labelConformeDirector = formatConformeEtiqueta(
    capActiva.fecha_validacion_director,
    capActiva.validador_director_nombre,
    'director',
  );
  const origenMultimedia = origenMultimediaCaptacion(capActiva);
  // Si hay fotos ya curadas para Marco, mostrar contenedores aunque el flag se haya pisado a escritorio.
  const tieneCuracionMarco = fotosReportaje.some((f) => f.en_seleccion_marco === true);
  // Para Revisar: siempre UI de curación (Todas | Selección Marco) cuando hay reportaje
  // o origen móvil. Nunca parrilla suelta en secretaría (obliga a scroll y tapa textos).
  const esOrigenMovil = origenMultimedia === 'movil'
    || tieneCuracionMarco
    || fotosReportaje.length > 0;
  const esOrigenEscritorio = origenMultimedia === 'escritorio' && !esOrigenMovil;
  const guardandoAlgo = guardandoSecretaria || guardandoDirector || guardandoExtras || guardandoCompletar;

  const textosWebPendientes = !tituloCaptacion({
    ...(capFresh ? parseExtrasCap(capFresh) : {}),
    ...valoresExtras,
  })
    || !String(
      valoresExtras.web_desc_larga_es
      ?? (capFresh ? parseExtrasCap(capFresh).web_desc_larga_es : '')
      ?? '',
    ).trim();

  const fichaCaptacionGenerandoUi = fichaCaptacionGenerando || !!extrasCap.ficha_captacion_generando;
  const fichaCaptacionPendiente = esOrigenMovil && fichaCaptacionGenerandoUi;
  const certCatastroGenerandoUi = certCatastroGenerando || !!extrasCap.catastro_cert_generando;
  const certCatastroPendiente = certCatastroGenerandoUi;
  const certCatastroErrorUi = certCatastroError || extrasCap.catastro_cert_error || '';
  const hayDesviacionCatastro = !!extrasCap.catastro_desviaciones?.hayDesviacion;

  const resolverHrefDocumento = (doc) => {
    let hrefBase = absolutizarMediaUrl(doc.url);
    // Ficha: preferir URL canónica de extras (la regenerada), no una copia cacheada.
    if (esDocFichaCaptacion(doc)) {
      const canon = absolutizarMediaUrl(extrasCap.ficha_captacion_url);
      if (canon) hrefBase = canon;
    }
    const bust = encodeURIComponent(
      `${esDocFichaCaptacion(doc) ? (extrasCap.ficha_captacion_generada_at || '') : ''}|${hrefBase || ''}`,
    );
    const href = hrefBase
      ? `${hrefBase}${hrefBase.includes('?') ? '&' : '?'}v=${bust}`
      : '';
    return { hrefBase, href };
  };

  // Chip compacto en línea (cabecera): icono + nombre corto + lupa diminuta, sin salto de línea.
  const renderChipDocInline = (doc, { keyPrefix = 'doc', icon = '📄', colorText, colorBg, colorBorder } = {}) => {
    const { hrefBase, href } = resolverHrefDocumento(doc);
    const titulo = doc.nombre_documento || doc.nombre_original || 'Documento';
    const tituloCorto = titulo.length > 22 ? `${titulo.slice(0, 20)}…` : titulo;
    return (
      <span key={`${keyPrefix}-${doc.id || titulo}-${hrefBase || ''}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginLeft: 8, verticalAlign: 'middle' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: colorText }} title={titulo}>{icon} {tituloCorto}</span>
        <button
          type="button"
          disabled={!href}
          title={href ? 'Ver documento' : 'Sin URL'}
          aria-label={`Ver documento: ${titulo}`}
          onClick={(e) => {
            e.stopPropagation();
            if (!href) return;
            const live = hrefBase ? `${hrefBase}${hrefBase.includes('?') ? '&' : '?'}t=${Date.now()}` : '';
            setDocPreview({ title: titulo, src: live, kind: docVistaPreviaKind(doc, hrefBase || live) });
          }}
          style={{
            flexShrink: 0, width: 18, height: 18, borderRadius: 5, padding: 0,
            border: `1px solid ${colorBorder}`, background: colorBg, color: colorText,
            cursor: href ? 'pointer' : 'not-allowed', opacity: href ? 1 : 0.4,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <IconoLupa size={10} />
        </button>
      </span>
    );
  };

  const renderFilaDocumento = (doc, { keyPrefix = 'doc', icon = '📄', compact = false } = {}) => {
    const { hrefBase, href } = resolverHrefDocumento(doc);
    const titulo = doc.nombre_documento || doc.nombre_original || 'Documento';
    return (
      <div
        key={`${keyPrefix}-${doc.id || titulo}-${hrefBase || ''}`}
        style={{
          display: 'flex', alignItems: 'center', gap: compact ? 6 : 8,
          marginBottom: compact ? 4 : 6, padding: compact ? '6px 8px' : '8px 10px', borderRadius: compact ? 6 : 8,
          border: `1px solid ${c.border}`, background: c.bgInput,
          color: compact ? c.textMuted : c.textPrimary, fontSize: compact ? 11 : 12,
        }}
      >
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={titulo}>
          {icon} {titulo}
        </span>
        <button
          type="button"
          disabled={!href}
          title={href ? 'Ver documento' : 'Sin URL'}
          aria-label={`Ver documento: ${titulo}`}
          onClick={() => {
            if (!href) return;
            // Bust en el momento del click (evita iframe PDF cacheado).
            const live = hrefBase
              ? `${hrefBase}${hrefBase.includes('?') ? '&' : '?'}t=${Date.now()}`
              : '';
            setDocPreview({ title: titulo, src: live, kind: docVistaPreviaKind(doc, hrefBase || live) });
          }}
          style={{
            flexShrink: 0, width: compact ? 32 : 36, height: compact ? 32 : 36, borderRadius: compact ? 6 : 8,
            border: `1px solid ${c.border}`, background: c.bgCard, color: ml.purple,
            cursor: href ? 'pointer' : 'not-allowed', opacity: href ? 1 : 0.45,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <IconoLupa size={compact ? 14 : 16} />
        </button>
      </div>
    );
  };

  const publicarWebChecked =
    valoresExtras.publicar_web === true ||
    valoresExtras.publicar_web === 'true' ||
    valoresExtras.publicar_web === 1 ||
    valoresExtras.publicar_web === '1' ||
    ((valoresExtras.publicar_web === undefined || valoresExtras.publicar_web === null || valoresExtras.publicar_web === '') &&
      (extrasCap.publicar_web === true || extrasCap.publicar_web === 'true' || extrasCap.publicar_web === 1 || extrasCap.publicar_web === '1'));
  const exclusivaRaw = (valoresExtras.exclusiva !== undefined && valoresExtras.exclusiva !== null && valoresExtras.exclusiva !== '')
    ? valoresExtras.exclusiva
    : extrasCap.exclusiva;
  const exclusivaActiva = exclusivaRaw === true || exclusivaRaw === 'true' || exclusivaRaw === 1 || exclusivaRaw === '1';
  const camposExclusiva = CAMPOS_EXCLUSIVA_DEP;
  const camposExclusivaExtra = exclusivaActiva
    ? camposCompletos.filter((c) => camposExclusiva.includes(c.campo_key))
    : [];
  // Ampliar: duración y vencimiento siempre exigibles con exclusiva (aunque no vengan en BD O).
  const SYN_EXCL = [
    { campo_key: 'exclusiva_duracion', campo_label: 'Duración exclusiva', campo_grupo: 'cabecera' },
    { campo_key: 'exclusiva_hasta', campo_label: 'Vencimiento exclusiva', campo_grupo: 'cabecera' },
  ];
  const synExclFaltan = exclusivaActiva
    ? SYN_EXCL.filter((c) => {
        const raw = valoresExtras[c.campo_key] ?? extrasCap[c.campo_key]
          ?? (c.campo_key === 'exclusiva_duracion' ? (valoresExtras.duracion_exclusiva ?? extrasCap.duracion_exclusiva) : undefined);
        return raw === undefined || raw === null || String(raw).trim() === '';
      })
    : [];
  const camposEdicion = [
    ...camposObligatorios,
    ...camposExclusivaExtra.filter((c) => !camposObligatorios.some((f) => f.campo_key === c.campo_key)),
    ...synExclFaltan.filter((c) =>
      !camposObligatorios.some((f) => f.campo_key === c.campo_key)
      && !camposExclusivaExtra.some((f) => f.campo_key === c.campo_key)
    ),
  ];
  let camposObligatoriosVisibles = camposEdicion.filter((c) => {
    if (CAMPOS_WEB_SECRETARIA.has(c.campo_key)) return false;
    if (c.campo_key === 'publicar_web' || c.campo_key === 'visible_web') return false;
    if (c.campo_key === 'fotos') return false;
    // precio_venta es columna nativa; ya hay bloque fijo "Precio de venta *" abajo
    if (c.campo_key === 'precio_venta') return false;
    if (c.campo_key === 'honorarios_porcentaje') return false;
    if (CAMPOS_EXCLUSIVA_DEP.includes(c.campo_key) && !exclusivaActiva) return false;
    return true;
  });
  if (camposObligatorios.length > 0 && camposObligatoriosVisibles.length === 0) {
    camposObligatoriosVisibles = camposObligatorios.filter((c) => {
      if (CAMPOS_WEB_SECRETARIA.has(c.campo_key)) return false;
      if (c.campo_key === 'publicar_web' || c.campo_key === 'visible_web') return false;
      if (c.campo_key === 'fotos' || c.campo_key === 'precio_venta') return false;
      if (CAMPOS_EXCLUSIVA_DEP.includes(c.campo_key) && !exclusivaActiva) return false;
      return true;
    });
  }
  const camposOrdenados = (() => {
    const dependientes = new Set(CAMPOS_EXCLUSIVA_DEP);
    const depsMap = new Map(camposObligatoriosVisibles.filter((c) => dependientes.has(c.campo_key)).map((c) => [c.campo_key, c]));
    const base = camposObligatoriosVisibles.filter((c) => !dependientes.has(c.campo_key));
    const out = [];
    base.forEach((c) => {
      out.push(c);
      if (c.campo_key === 'exclusiva') {
        CAMPOS_EXCLUSIVA_DEP.forEach((k) => { if (depsMap.has(k)) out.push(depsMap.get(k)); });
      }
    });
    return out;
  })();

  const buildExtrasPayload = () => {
    const exclusivaRaw = valoresExtras.exclusiva ?? extrasCap.exclusiva;
    const exclusivaActiva = exclusivaRaw === true || exclusivaRaw === 'true' || exclusivaRaw === 1 || exclusivaRaw === '1';
    const payload = {};
    Object.entries(valoresExtras).forEach(([k, v]) => {
      if (k === 'fotos') return; // nunca reenviar base64 del móvil
      if (CAMPOS_EXCLUSIVA_DEP.includes(k) && !exclusivaActiva) return;
      if (typeof v === 'string' && v.startsWith('data:image')) return;
      if (Array.isArray(v) && v[0] && (typeof v[0] === 'string' ? String(v[0]).startsWith('data:') : v[0]?.dataUrl)) return;
      if (isMoneyField(k)) {
        const money = parseMoney(v);
        if (money !== null) payload[k] = money;
        return;
      }
      if (v !== '' && v !== null && v !== undefined) payload[k] = v;
    });
    return payload;
  };

  const refrescarCaptacion = async () => {
    try {
      const capRes = await api.get(`/captaciones/${captacion.id}`);
      const freshCap = capRes.data?.data || capRes.data || captacion;
      setCapFresh(freshCap);
      return freshCap;
    } catch {
      return capFresh || captacion;
    }
  };

  const postConformeCaptacion = async (paso, body) => {
    const id = captacion.id;
    const intentos = [
      () => api.post(`/captaciones/${id}/validar`, { paso, ...body }),
      () => api.post(`/captaciones/${id}/conforme-${paso}`, body),
      () => api.post(`/captaciones/${id}/conforme/${paso}`, body),
    ];
    let lastErr;
    for (const intento of intentos) {
      try {
        return await intento();
      } catch (err) {
        lastErr = err;
        const status = err.response?.status;
        const msg = err.response?.data?.error || '';
        if (status === 404 || msg === 'Ruta no encontrada') continue;
        throw err;
      }
    }
    throw lastErr;
  };

  const aplicarResultadoValidacion = async (data) => {
    await refrescarCaptacion();
    try { await onValidado?.({ parcial: true }); } catch {}
    if (data.finalizado) {
      setReferencia(data.referencia || '');
      setValidado(true);
      setError('');
      setMsgConforme('');
      setTimeout(async () => {
        try { await onValidado?.({ finalizado: true }); } catch {}
        onClose?.();
      }, data.reanudada ? 1500 : 2500);
      return true;
    }
    if (data.error) {
      setMsgConforme('');
      setError(data.error);
    }
    return false;
  };

  const handleCompletarValidacion = async () => {
    setError('');
    setMsgConforme('');
    setGuardandoCompletar(true);
    try {
      const res = await api.post(`/captaciones/${captacion.id}/validar`, {});
      if (await aplicarResultadoValidacion(res.data || {})) return;
      setError(res.data?.error || 'No se pudo completar la validación');
    } catch (err) {
      setError(err.response?.data?.error || 'Error al completar la validación');
    } finally {
      setGuardandoCompletar(false);
    }
  };

  const handleConformeSecretaria = async () => {
    setError('');
    setCamposFaltantes(null);
    setMsgConforme('');
    const extrasWeb = capFresh ? parseExtrasCap(capFresh) : {};
    const tituloTrim = tituloCaptacion({ ...extrasWeb, ...valoresExtras });
    if (!tituloTrim) { setError('El título es obligatorio'); return; }
    const descLarga = String(
      valoresExtras.web_desc_larga_es ?? extrasWeb.web_desc_larga_es ?? '',
    ).trim();
    if (!descLarga) {
      setError('Falta la descripción larga. Se genera al pasar a Para Revisar; espera unos segundos o pulsa «Regenerar».');
      return;
    }
    const tl = valoresExtras.tenemos_llaves ?? extrasWeb.tenemos_llaves ?? extrasCap.tenemos_llaves;
    const tenemosLlaves = tl === true || tl === 'true' || tl === 1 || tl === '1';
    const numLlave = String(
      valoresExtras.num_llave ?? extrasWeb.num_llave ?? extrasCap.num_llave ?? '',
    ).trim();
    if (tenemosLlaves && !numLlave) {
      setCamposFaltantes(['Nº de llave']);
      return;
    }

    setGuardandoSecretaria(true);
    try {
      const payload = buildExtrasPayload();
      if (tituloTrim) {
        payload.titulo = tituloTrim;
        payload.web_titulo = tituloTrim;
      }
      if (descLarga) payload.web_desc_larga_es = descLarga;
      const descCorta = String(
        valoresExtras.web_desc_corta_es ?? extrasWeb.web_desc_corta_es ?? '',
      ).trim();
      if (descCorta) payload.web_desc_corta_es = descCorta;
      if (tenemosLlaves) {
        payload.tenemos_llaves = true;
        payload.num_llave = numLlave;
      }
      const res = await postConformeCaptacion('secretaria', { datos: payload });
      const data = res.data || {};
      if (await aplicarResultadoValidacion(data)) return;
      if (data.ya_conforme) {
        setMsgConforme(data.pendiente === 'director'
          ? 'La secretaría ya había dado conforme. Falta la validación del director.'
          : 'La secretaría ya había dado conforme.');
      } else {
        setMsgConforme('Conforme de secretaría registrado. Puedes cerrar el modal; falta la validación del director.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error al registrar conformidad de secretaría');
    } finally {
      setGuardandoSecretaria(false);
    }
  };

  const handleConformeDirector = async () => {
    setError('');
    setCamposFaltantes(null);
    setMsgConforme('');
    const pvNum = parsePrecioVentaEntero(precioVentaStr);
    const faltantesDirector = [];
    if (!Number.isFinite(pvNum) || pvNum <= 0) faltantesDirector.push('Precio de venta');
    if (calificacion == null || calificacion < 0 || calificacion > 3) faltantesDirector.push('Calificación');
    if (faltantesDirector.length > 0) {
      setCamposFaltantes(faltantesDirector);
      return;
    }
    const tituloTrim = tituloCaptacion({ ...extrasCap, ...valoresExtras });
    if (!tituloTrim) { setError('El título es obligatorio'); return; }

    setGuardandoDirector(true);
    try {
      const honPayload = {};
      [...HON_MERGE, 'titulo', 'web_titulo'].forEach((k) => {
        const v = valoresExtras[k];
        if (v !== '' && v != null && v !== undefined) honPayload[k] = isMoneyField(k) ? parseMoney(v) ?? v : v;
      });
      const res = await postConformeCaptacion('director', {
        precio_venta: pvNum,
        calificacion,
        titulo: tituloTrim,
        datos: honPayload,
      });
      const data = res.data || {};
      if (await aplicarResultadoValidacion(data)) return;
      if (data.ya_conforme) {
        setMsgConforme(data.pendiente === 'secretaria'
          ? 'El director ya había dado conforme. Falta la validación de secretaría.'
          : 'El director ya había dado conforme.');
      } else {
        setMsgConforme('Conforme de director registrado. Puedes cerrar el modal; falta la validación de secretaría.');
      }
    } catch (err) {
      const errData = err.response?.data;
      if (errData?.ya_validada) {
        setError('Esta captación ya fue validada. Usa el botón «Ficha completa» para abrirla.');
      } else {
        setError(errData?.error || 'Error al registrar conformidad de director');
      }
    } finally {
      setGuardandoDirector(false);
    }
  };

  const handleArchivar = async () => {
    setArchivando(true);
    setError('');
    try {
      const etapaDestino = accionArchivo === 'frio' ? 'fria' : 'eliminado';
      await api.patch(`/captaciones/${captacion.id}/etapa`, {
        etapa: etapaDestino,
        motivo: motivoArchivo || null,
        historial_data: {
          tipo: 'cambio_etapa',
          contenido: etapaDestino === 'fria'
            ? (motivoArchivo || 'Enviada a Fríos desde Para Revisar')
            : (motivoArchivo || 'Eliminada desde Para Revisar'),
          metadata: {
            etapa_origen: captacion.etapa || 'revisar_cap',
            motivo: motivoArchivo || null,
          },
        },
      });
      setAccionArchivo(null);
      onValidado?.();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al archivar');
    } finally {
      setArchivando(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '8px 12px', fontSize: 13,
    border: `1px solid ${c.border}`, borderRadius: 8,
    background: c.bgInput, color: c.textPrimary, outline: 'none',
    fontFamily: 'inherit', transition: 'border-color 0.15s', boxSizing: 'border-box',
  };
  const labelStyle = {
    display: 'block', marginBottom: 5, fontSize: 10, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.08em',
    color: isDark ? '#4ade80' : '#16a34a',
  };

  const ml = {
    purple: '#8b5cf6',
    purpleFrom: '#7c3aed',
    purpleTo: '#8b5cf6',
    purpleShadow: 'rgba(139,92,246,0.35)',
    purpleText: '#ede9fe',
    // Etiquetas de sección: verde legible sobre fondo oscuro
    labelAccent: isDark ? '#4ade80' : '#16a34a',
    // Cian para la Ficha de Captación, legible tanto en claro como en oscuro
    cyan: isDark ? '#22d3ee' : '#0891b2',
    // Esmeralda para Cert. gráfica catastral
    emerald: isDark ? '#34d399' : '#059669',
  };

  // Misma altura para las tres tarjetas de fotos (Todas las fotos / Selección Marco / Fotos seleccionadas)
  const ALTO_TARJETA_FOTOS = 104;

  return (
    <>
    <div onClick={() => { if (!guardandoAlgo) solicitarCierreRevisar(); }} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, fontFamily: "'DM Sans', sans-serif",
    }}>
      <div ref={dragRef} onClick={e => e.stopPropagation()} style={{
        width: 'min(1120px, 98vw)', height: 'min(920px, 96vh)', maxHeight: '96vh', display: 'flex', flexDirection: 'column',
        background: isDark ? c.bgCard : '#c8c8be', borderRadius: 16, overflow: 'hidden',
        border: `1px solid ${isDark ? c.border : '#a8a8a0'}`,
        boxShadow: isDark
          ? '0 0 60px 15px rgba(255,255,255,0.18), 0 0 120px 40px rgba(255,255,255,0.10)'
          : '0 16px 48px rgba(0,0,0,0.30), 0 4px 12px rgba(0,0,0,0.15)',
        position: pos ? 'fixed' : 'relative',
        ...(pos ? { left: pos.x, top: pos.y, margin: 0 } : {}),
      }}>

        {/* Confirmación validado */}
          {validado && (
          <div style={{
            position: 'absolute', inset: 0, borderRadius: 14, background: c.bgCard,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 12, zIndex: 13000,
          }}>
            <div style={{ fontSize: 56 }}>🏠</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: ml.purple }}>¡Captación validada!</div>
            <div style={{ fontSize: 14, color: c.textMuted }}>Referencia asignada:</div>
            <div style={{ fontSize: 32, fontWeight: 900, color: c.textPrimary }}>{referencia}</div>
            <div style={{ fontSize: 13, color: c.textMuted }}>La propiedad ya está en cartera</div>
          </div>
        )}

        {/* Campos obligatorios faltantes al intentar dar conforme */}
        {camposFaltantes && camposFaltantes.length > 0 && (
          <div
            role="presentation"
            onClick={() => setCamposFaltantes(null)}
            style={{
              position: 'absolute', inset: 0, borderRadius: 14, background: 'rgba(0,0,0,0.55)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 13500,
            }}
          >
            <div onClick={(e) => e.stopPropagation()} style={{
              background: c.bgCard, borderRadius: 14, padding: '24px 28px', maxWidth: 340, width: '90%',
              border: `1px solid ${c.border}`, boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center',
            }}>
              <div style={{ fontSize: 38 }}>⚠️</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: c.textPrimary }}>Faltan datos obligatorios</div>
              <div style={{ fontSize: 13, color: c.textMuted }}>No se puede dar conforme hasta completar:</div>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
                {camposFaltantes.map((f) => (
                  <li key={f} style={{
                    fontWeight: 700, fontSize: 13, color: '#f59e0b',
                    padding: '6px 10px', borderRadius: 8,
                    background: isDark ? 'rgba(245,158,11,0.12)' : '#fffbeb',
                    border: '1px solid #f59e0b',
                  }}>{f}</li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => setCamposFaltantes(null)}
                style={{
                  marginTop: 6, padding: '10px 26px', borderRadius: 8, border: 'none',
                  background: `linear-gradient(135deg, ${ml.purpleFrom}, ${ml.purpleTo})`,
                  color: ml.purpleText, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >Entendido</button>
            </div>
          </div>
        )}

        {/* Desviación ficha vs Catastro */}
        {modalDesviacionCatastro?.hayDesviacion && (
          <div
            role="presentation"
            onClick={() => {
              setDesviacionCatastroDismissed(true);
              setModalDesviacionCatastro(null);
            }}
            style={{
              position: 'absolute', inset: 0, borderRadius: 14, background: 'rgba(0,0,0,0.55)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 13600,
            }}
          >
            <div onClick={(e) => e.stopPropagation()} style={{
              background: c.bgCard, borderRadius: 14, padding: '22px 24px', maxWidth: 520, width: '92%',
              border: `1px solid ${c.border}`, boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
              display: 'flex', flexDirection: 'column', gap: 12,
            }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: c.textPrimary, textAlign: 'center' }}>
                Desviación respecto a Catastro
              </div>
              <div style={{ fontSize: 12, color: c.textMuted, textAlign: 'center', lineHeight: 1.4 }}>
                {modalDesviacionCatastro.unidad
                  ? 'En pisos y apartamentos Marco exige certificado de la unidad (altura, puerta, bloque/escalera en la dirección), nunca de la finca matriz ni de un portal/nave entero.'
                  : modalDesviacionCatastro.declaracion
                  ? 'Casa de campo no declarada. Abajo, el contraste de m² si los hay, y el criterio de Marco sobre si se puede declarar por antigüedad catastral.'
                  : 'Hay diferencias entre la ficha y Catastro (±10% en m² o ±5 años), o la ficha no tiene un dato que Catastro sí aporta.'}
              </div>
              {modalDesviacionCatastro.declaracion?.texto && (
                <div style={{
                  fontSize: 13, lineHeight: 1.45, fontWeight: 600,
                  color: modalDesviacionCatastro.declaracion.acuerdo
                    ? (isDark ? '#86efac' : '#166534')
                    : (isDark ? '#fbbf24' : '#b45309'),
                  background: modalDesviacionCatastro.declaracion.acuerdo
                    ? (isDark ? 'rgba(34,197,94,0.12)' : '#f0fdf4')
                    : (isDark ? 'rgba(245,158,11,0.12)' : '#fffbeb'),
                  border: `1px solid ${modalDesviacionCatastro.declaracion.acuerdo ? '#22c55e' : '#f59e0b'}`,
                  borderRadius: 8, padding: '10px 12px',
                }}>
                  {modalDesviacionCatastro.declaracion.texto}
                </div>
              )}
              {modalDesviacionCatastro.unidad?.texto && (
                <div style={{
                  fontSize: 13, lineHeight: 1.45, fontWeight: 600,
                  color: modalDesviacionCatastro.unidad.acuerdo
                    ? (isDark ? '#86efac' : '#166534')
                    : (isDark ? '#fbbf24' : '#b45309'),
                  background: modalDesviacionCatastro.unidad.acuerdo
                    ? (isDark ? 'rgba(34,197,94,0.12)' : '#f0fdf4')
                    : (isDark ? 'rgba(245,158,11,0.12)' : '#fffbeb'),
                  border: `1px solid ${modalDesviacionCatastro.unidad.acuerdo ? '#22c55e' : '#f59e0b'}`,
                  borderRadius: 8, padding: '10px 12px',
                }}>
                  {modalDesviacionCatastro.unidad.texto}
                </div>
              )}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: c.textMuted }}>
                    <th style={{ padding: '6px 4px', fontWeight: 600 }}>Campo</th>
                    <th style={{ padding: '6px 4px', fontWeight: 600 }}>Ficha</th>
                    <th style={{ padding: '6px 4px', fontWeight: 600 }}>Catastro</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { key: 'ano', label: 'Año construcción' },
                    { key: 'm2_parcela', label: 'm² parcela' },
                    { key: 'm2_construidos', label: 'm² construidos' },
                  ].map(({ key, label }) => {
                    const row = modalDesviacionCatastro[key];
                    if (!row) return null;
                    return (
                      <tr key={key} style={{
                        color: row.alerta ? (isDark ? '#fbbf24' : '#b45309') : c.textPrimary,
                        fontWeight: row.alerta ? 700 : 500,
                      }}>
                        <td style={{ padding: '6px 4px', borderTop: `1px solid ${c.border}` }}>{label}</td>
                        <td style={{ padding: '6px 4px', borderTop: `1px solid ${c.border}` }}>{row.ficha ?? '—'}</td>
                        <td style={{ padding: '6px 4px', borderTop: `1px solid ${c.border}` }}>{row.catastro ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <button
                type="button"
                onClick={() => {
                  setDesviacionCatastroDismissed(true);
                  setModalDesviacionCatastro(null);
                }}
                style={{
                  marginTop: 4, padding: '10px 26px', borderRadius: 8, border: 'none', alignSelf: 'center',
                  background: `linear-gradient(135deg, ${ml.purpleFrom}, ${ml.purpleTo})`,
                  color: ml.purpleText, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >Entendido</button>
            </div>
          </div>
        )}

        {/* Barra morada superior */}
        <div style={{ height: 3, flexShrink: 0, background: `linear-gradient(90deg, #a78bfa, ${ml.purple}, ${ml.purpleFrom}, ${ml.purple}, #a78bfa)` }} />

        {/* Header */}
        <div onMouseDown={startDrag} style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, cursor: 'grab', userSelect: 'none', background: isDark ? 'transparent' : '#e0e0d8', borderBottom: `1px solid ${isDark ? c.border : '#a8a8a0'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: `linear-gradient(135deg, ${ml.purpleFrom}, ${ml.purpleTo})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 4px 12px ${ml.purpleShadow}`,
            }}>
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round">
                <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
              </svg>
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: c.textPrimary }}>Revisar captación</h2>
                {esPrueba && (
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
                    background: 'rgba(245,158,11,0.35)',
                    color: isDark ? '#ffffff' : '#000000',
                    border: '1px solid rgba(245,158,11,0.55)',
                  }}>
                    🧪 Prueba · ViaFirma sandbox
                  </span>
                )}
              </div>
              <p style={{ margin: 0, marginTop: 1, fontSize: 12, color: '#fff' }}>
                {capActiva.nombre ? `${capActiva.nombre} ${capActiva.apellido || ''}`.trim() : (capActiva.calle || 'Sin nombre')}
                {' · '}{tipoInmuebleLegible(capActiva, extrasCap) || '—'}
                {refManual && (
                  <span style={{ marginLeft: 6, fontWeight: 700, color: c.accentBlue }}>· Ref. {refManual}</span>
                )}
                {direccionPipelineCard(capActiva, { ...extrasCap, ...valoresExtras }) && (
                  <span style={{ marginLeft: 6, fontWeight: 600, color: isDark ? '#c4b5fd' : '#ede9fe' }}>
                    · 📍 {direccionPipelineCard(capActiva, { ...extrasCap, ...valoresExtras })}
                  </span>
                )}
                {(onOpenFichaOverlay || onOpenFicha) && (
                  <>
                    {' · '}
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        const capParaFicha = capFresh || captacion;
                        if (onOpenFichaOverlay) onOpenFichaOverlay(capParaFicha);
                        else if (onOpenFicha) {
                          onClose?.();
                          onOpenFicha(capParaFicha);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          const capParaFicha = capFresh || captacion;
                          if (onOpenFichaOverlay) onOpenFichaOverlay(capParaFicha);
                          else if (onOpenFicha) {
                            onClose?.();
                            onOpenFicha(capParaFicha);
                          }
                        }
                      }}
                      style={{ color: ml.labelAccent, cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}
                    >Ver ficha completa</span>
                  </>
                )}
                {documentosClasificados.encargos.length > 0
                  ? documentosClasificados.encargos.map((doc) => renderChipDocInline(doc, {
                    keyPrefix: 'head-enc', icon: '✅',
                    colorText: isDark ? '#fbbf24' : '#d97706',
                    colorBg: isDark ? 'rgba(245,158,11,0.18)' : '#fef3c7',
                    colorBorder: isDark ? 'rgba(245,158,11,0.45)' : '#fcd34d',
                  }))
                  : extrasCap.documento_firmado_url && renderChipDocInline(
                    { id: 'encargo-extras', nombre_documento: 'Encargo de venta firmado', url: extrasCap.documento_firmado_url },
                    {
                      keyPrefix: 'head-enc-fallback', icon: '✅',
                      colorText: isDark ? '#fbbf24' : '#d97706',
                      colorBg: isDark ? 'rgba(245,158,11,0.18)' : '#fef3c7',
                      colorBorder: isDark ? 'rgba(245,158,11,0.45)' : '#fcd34d',
                    },
                  )}
                {fichaCaptacionDocsUi.map((doc) => renderChipDocInline(doc, {
                  keyPrefix: 'head-ficha', icon: '📋',
                  colorText: ml.cyan,
                  colorBg: isDark ? 'rgba(34,211,238,0.18)' : '#cffafe',
                  colorBorder: isDark ? 'rgba(34,211,238,0.45)' : '#67e8f9',
                }))}
                {fichaCaptacionPendiente && (
                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: ml.labelAccent }}>
                    · {fichaCaptacionDocsUi.length > 0 ? 'Actualizando ficha…' : 'Generando ficha…'}
                  </span>
                )}
                {esOrigenMovil && fichaCaptacionDocsUi.length === 0 && !fichaCaptacionPendiente && fichaCaptacionError && (
                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: isDark ? '#fca5a5' : '#b91c1c' }} title={fichaCaptacionError}>
                    · Error al generar ficha
                  </span>
                )}
                {certCatastroDocsUi.map((doc) => renderChipDocInline(doc, {
                  keyPrefix: 'head-cert-cat', icon: '🗺️',
                  colorText: ml.emerald,
                  colorBg: isDark ? 'rgba(52,211,153,0.18)' : '#d1fae5',
                  colorBorder: isDark ? 'rgba(52,211,153,0.45)' : '#6ee7b7',
                }))}
                {certCatastroPendiente && (
                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: ml.emerald }}>
                    · {certCatastroDocsUi.length > 0 ? 'Actualizando ficha catastral…' : 'Generando ficha catastral…'}
                  </span>
                )}
                {!certCatastroPendiente && certCatastroDocsUi.length === 0 && certCatastroErrorUi && (
                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: isDark ? '#fca5a5' : '#b91c1c' }} title={certCatastroErrorUi}>
                    · Ficha catastral pendiente
                  </span>
                )}
                {!certCatastroPendiente && certCatastroDocsUi.length === 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      asegurarCertGraficaCatastral({ forzar: true });
                    }}
                    style={{
                      marginLeft: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                      color: ml.emerald,
                      background: isDark ? 'rgba(52,211,153,0.12)' : '#ecfdf5',
                      border: `1px solid ${isDark ? 'rgba(52,211,153,0.4)' : '#6ee7b7'}`,
                      borderRadius: 6, padding: '2px 8px',
                    }}
                    title={certCatastroOmitidoUi
                      ? 'Se omitió tras borrarla. Generar de nuevo (p. ej. tras corregir la ref. catastral).'
                      : 'Generar ficha gráfica catastral'}
                  >
                    🗺️ Generar ficha catastral
                  </button>
                )}
                {hayDesviacionCatastro && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDesviacionCatastroDismissed(false);
                      setModalDesviacionCatastro(extrasCap.catastro_desviaciones);
                    }}
                    style={{
                      marginLeft: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                      color: isDark ? '#fbbf24' : '#b45309',
                      background: isDark ? 'rgba(245,158,11,0.15)' : '#fffbeb',
                      border: `1px solid ${isDark ? 'rgba(245,158,11,0.45)' : '#fcd34d'}`,
                      borderRadius: 6, padding: '2px 8px',
                    }}
                    title="Ver desviaciones ficha vs Catastro"
                  >
                    ⚠ Desviación Catastro
                  </button>
                )}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {captacion.propiedad_id && (
              <button onClick={() => window.open(`/propiedades/${captacion.propiedad_id}/editar`, '_blank')}
                title="Abrir ficha completa"
                style={{
                  padding: '6px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                  border: `1px solid ${c.border}`, background: c.bgInput, color: c.textMuted,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}>
                ✏️ Ficha completa
              </button>
            )}
            <button onClick={() => { if (!guardandoAlgo) solicitarCierreRevisar(); }} style={{
              width: 32, height: 32, borderRadius: 8, border: `1px solid ${c.border}`,
              background: c.bgInput, cursor: 'pointer', fontSize: 16, color: c.textMuted,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>✕</button>
          </div>
        </div>

        <div style={{ height: 1, flexShrink: 0, background: `linear-gradient(90deg, transparent, ${c.border}, transparent)`, margin: '16px 24px 0' }} />

        {capActiva.propiedad_id && !capActiva.fecha_validacion && (
          <div style={{
            margin: '12px 24px 0', padding: '10px 14px', borderRadius: 10,
            background: isDark ? 'rgba(245,158,11,0.12)' : '#fffbeb',
            border: '1px solid #f59e0b', fontSize: 12, lineHeight: 1.45, color: isDark ? '#fcd34d' : '#92400e',
          }}>
            Hay un borrador de propiedad vinculado (ref. manual). Cuando secretaría y director den conforme se completará el alta,
            se sincronizarán todas las fotos y Marco las procesará juntas en una sola carpeta Autoenhance.
            Hasta entonces Marco no debe editar ni publicar en web.
          </div>
        )}

        {(yaConformeSecretaria || yaConformeDirector) && !capActiva.fecha_validacion && (
          <div style={{
            margin: '12px 24px 0', padding: '10px 14px', borderRadius: 10,
            background: pendienteCompletarValidacion
              ? (isDark ? 'rgba(245,158,11,0.12)' : '#fffbeb')
              : (isDark ? 'rgba(59,130,246,0.12)' : '#eff6ff'),
            border: pendienteCompletarValidacion
              ? '1px solid #f59e0b'
              : `1px solid ${isDark ? 'rgba(59,130,246,0.35)' : '#93c5fd'}`,
            fontSize: 12, lineHeight: 1.5,
            color: pendienteCompletarValidacion
              ? (isDark ? '#fcd34d' : '#92400e')
              : (isDark ? '#93c5fd' : '#1e40af'),
          }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              {pendienteCompletarValidacion ? 'Falta completar el alta en cartera' : 'Validación en dos pasos'}
            </div>
            {labelConformeSecretaria && <div>✓ {labelConformeSecretaria}</div>}
            {labelConformeDirector && <div>✓ {labelConformeDirector}</div>}
            {!yaConformeSecretaria && <div style={{ marginTop: 4 }}>Pendiente: conformidad de secretaría</div>}
            {!yaConformeDirector && <div style={{ marginTop: 4 }}>Pendiente: conformidad de director</div>}
            {pendienteCompletarValidacion ? (
              <>
                <div style={{ marginTop: 6, opacity: 0.95 }}>
                  Secretaría y director ya han validado, pero el sistema no pudo cerrar la captación automáticamente.
                </div>
                {ultimoErrorValidacion && (
                  <div style={{ marginTop: 8, fontWeight: 600 }}>{ultimoErrorValidacion}</div>
                )}
                <button
                  type="button"
                  onClick={handleCompletarValidacion}
                  disabled={guardandoCompletar}
                  style={{
                    marginTop: 12, width: '100%', padding: '10px 16px', borderRadius: 8, border: 'none',
                    background: guardandoCompletar ? c.bgInput : '#f59e0b',
                    color: guardandoCompletar ? c.textMuted : '#fff',
                    cursor: guardandoCompletar ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                  }}
                >
                  {guardandoCompletar ? 'Completando validación…' : 'Completar validación'}
                </button>
              </>
            ) : (
              <div style={{ marginTop: 6, opacity: 0.9 }}>La captación permanece en «Para Revisar» hasta que ambos hayan validado.</div>
            )}
          </div>
        )}

        {/* Cuerpo: cada columna hace scroll interno; botones de conforme quedan accesibles */}
        <div style={{ flex: 1, overflow: 'hidden', minHeight: 0, padding: '0 6px' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
            gap: 0,
            alignItems: 'stretch',
            height: '100%',
            minHeight: 0,
          }}>
            <div style={{
              borderRight: `1px solid ${isDark ? c.border : '#a8a8a0'}`,
              padding: '4px 10px 12px 4px',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 0,
            }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: ml.labelAccent, marginBottom: 6, flexShrink: 0 }}>Secretaria</div>

              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', paddingRight: 2 }}>

          {/* Fotos: siempre dos contenedores en línea (Todas | Selección Marco), como el director.
              Sin parrilla suelta: obliga a scroll y tapa textos web. */}
          {verRevFotosSec && (
          <div ref={fotosSeccionRef} style={{ padding: '0 0 6px', opacity: modRevFotosSec ? 1 : 0.85 }} title={!modRevFotosSec ? tituloSinPermisoRev : undefined}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5, flexWrap: 'wrap', gap: 6 }}>
              <span style={{ ...labelStyle, marginBottom: 0, color: ml.labelAccent }}>
                Fotos del inmueble ({contadoresFotos.total})
              </span>
              {contadoresFotos.total > 0 && (
                <span style={{ fontSize: 10, color: c.textMuted, fontWeight: 600 }}>
                  {contadoresFotos.total} originales · {contadoresFotos.seleccionMarco} Marco · {contadoresFotos.web} Web
                </span>
              )}
              {subiendoFotos && <span style={{ fontSize: 11, color: ml.labelAccent }}>Subiendo...</span>}
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'nowrap' }}>
              <button
                type="button"
                title="Todas las fotos"
                onClick={() => {
                  if (!fotosReportaje.length) return;
                  abrirLightboxFoto(0, false, 'todas');
                }}
                style={{
                  position: 'relative',
                  flex: '1 1 0', minWidth: 0, height: ALTO_TARJETA_FOTOS, borderRadius: 12, cursor: fotosReportaje.length ? 'pointer' : 'not-allowed', fontFamily: 'inherit',
                  border: `2px solid ${prioridadSeleccion === 'todas' ? ml.labelAccent : c.border}`,
                  background: isDark ? c.bgInput : '#fff',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
                  color: c.text, padding: '8px 10px', overflow: 'hidden', opacity: fotosReportaje.length ? 1 : 0.5,
                }}
              >
                {prioridadSeleccion === 'todas' && (
                  <span style={{ position: 'absolute', top: 4, right: 6, fontSize: 8, fontWeight: 800, color: ml.labelAccent, letterSpacing: '0.04em' }}>PRIORIDAD</span>
                )}
                <span style={{
                  width: 36, height: 36, borderRadius: 9,
                  background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, flexShrink: 0,
                  boxShadow: '0 4px 12px rgba(124,58,237,0.35)',
                }}>🖼️</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: isDark ? '#fff' : '#0f172a', lineHeight: 1.15 }}>Todas las fotos</span>
                <span style={{ fontSize: 15, fontWeight: 800, color: isDark ? '#fff' : '#0f172a', letterSpacing: '0.02em', lineHeight: 1.15 }}>
                  {contadoresFotos.total} fotos
                </span>
              </button>
              <button
                type="button"
                title="Selección Marco"
                disabled={!fotosReportaje.length}
                onClick={async () => {
                  let lista = fotosReportaje.filter((f) => f.en_seleccion_marco === true);
                  if (!lista.length) {
                    if (seleccionMarcoEstado !== 'ok' && fotosReportaje.length > 0 && fotosReportaje.length <= 2) {
                      for (const f of fotosReportaje) {
                        if (f.id) {
                          await new Promise((resolve) => {
                            guardarCuracionFoto(f.id, { en_seleccion_marco: true, usar_web: true });
                            resolve();
                          });
                        }
                      }
                      setFotosReportaje((prev) => prev.map((f) => ({ ...f, en_seleccion_marco: true, usar_web: true })));
                      setSeleccionMarcoEstado('ok');
                      lista = fotosReportaje;
                    } else if (seleccionMarcoEstado === 'running' || seleccionMarcoEstado === 'pendiente') {
                      try {
                        await api.post(`/captaciones/${captacion.id}/reintentar-seleccion-marco`);
                      } catch { /* ignore */ }
                      setError('Marco aún está seleccionando fotos… (reintentando)');
                      cargarFotosReportaje();
                      return;
                    } else if (seleccionMarcoEstado === 'error') {
                      try {
                        await api.post(`/captaciones/${captacion.id}/reintentar-seleccion-marco`);
                      } catch { /* ignore */ }
                      setError('Selección Marco falló; reintentando…');
                      setSeleccionMarcoEstado('pendiente');
                      cargarFotosReportaje();
                      return;
                    } else {
                      // Sin selección (escritorio o aún no encolado): pedir Vision Marco
                      try {
                        await api.post(`/captaciones/${captacion.id}/reintentar-seleccion-marco`);
                        setSeleccionMarcoEstado('pendiente');
                        setError('Marco está seleccionando fotos…');
                        cargarFotosReportaje();
                      } catch {
                        setLightboxModoFotos('marco');
                        setInsertarMarcoOpen(true);
                      }
                      return;
                    }
                  }
                  abrirLightboxFoto(0, false, 'marco');
                }}
                style={{
                  position: 'relative',
                  flex: '1 1 0', minWidth: 0, height: ALTO_TARJETA_FOTOS, borderRadius: 12, cursor: fotosReportaje.length ? 'pointer' : 'not-allowed', fontFamily: 'inherit',
                  border: `2px solid ${prioridadSeleccion === 'marco' ? ml.labelAccent : c.border}`,
                  background: isDark ? c.bgInput : '#fff',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3,
                  color: c.text, padding: '6px 8px', opacity: fotosReportaje.length ? 1 : 0.5, overflow: 'hidden',
                }}
              >
                {prioridadSeleccion === 'marco' && (
                  <span style={{ position: 'absolute', top: 4, right: 6, fontSize: 8, fontWeight: 800, color: ml.labelAccent, letterSpacing: '0.04em' }}>PRIORIDAD</span>
                )}
                <span style={{
                  width: 36, height: 36, borderRadius: 9,
                  background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 18, flexShrink: 0,
                  boxShadow: '0 4px 12px rgba(124,58,237,0.35)',
                }}>🤖</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: isDark ? '#fff' : '#0f172a', lineHeight: 1.15 }}>Selección Marco</span>
                {seleccionMarcoEstado === 'error' ? (
                  <span style={{ fontSize: 13, fontWeight: 800, color: isDark ? '#fff' : '#0f172a', lineHeight: 1.15 }}>Pulsa para reintentar</span>
                ) : seleccionMarcoEstado === 'running' || seleccionMarcoEstado === 'pendiente' ? (
                  <span style={{ fontSize: 14, fontWeight: 800, color: isDark ? '#fff' : '#0f172a', lineHeight: 1.15 }}>Seleccionando…</span>
                ) : seleccionMarcoEstado === 'ok' || contadoresFotos.seleccionMarco > 0 ? (
                  <span style={{ fontSize: 12, fontWeight: 700, color: isDark ? '#fff' : '#0f172a', letterSpacing: '0.01em', lineHeight: 1.2, textAlign: 'center' }}>
                    {contadoresFotos.seleccionMarco} seleccionadas · {contadoresFotos.seleccionAEditar} a editar
                  </span>
                ) : (
                  <span style={{ fontSize: 14, fontWeight: 800, color: isDark ? '#fff' : '#0f172a', lineHeight: 1.15 }}>Pendiente</span>
                )}
              </button>
            </div>
            <input ref={inputFotoRef} type="file" accept="image/*" multiple onChange={handleSubirFotosReportaje} disabled={!modRevFotosSec} style={{ display: 'none' }} />
            <div style={{ fontSize: 10, color: c.textMuted, marginTop: 4, lineHeight: 1.35 }}>
              Abre «Todas las fotos» o «Selección Marco» para revisar, añadir o quitar. Marco valora frente a la selección actual.
            </div>
          </div>
          )}

          {verRevFotosSec && videosReportaje.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <span style={{ ...labelStyle, marginBottom: 4, color: ml.labelAccent, display: 'block' }}>Vídeos de reportaje ({videosReportaje.length})</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {videosReportaje.map((v) => (
                  <VideoThumbButton
                    key={v.id}
                    videoUrl={v.url}
                    thumbUrl={v.url_thumb}
                    border={c.border}
                    onClick={() => {
                      setVisorVideoError(null);
                      setVisorVideoUrl(absolutizarMediaUrl(v.url) || '__pending__');
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {verRevFotosSec && docsDocumentacionUi.length > 0 && (
            <div style={{ marginBottom: 14 }}>
            <span style={{ ...labelStyle, marginBottom: 6, color: ml.labelAccent, display: 'block' }}>Documentación en ficha</span>
            {docsDocumentacionUi.map((doc) => renderFilaDocumento(doc))}
            </div>
          )}

          {(() => {
            const tl = valoresExtras.tenemos_llaves ?? extrasCap.tenemos_llaves;
            const tenemosLlaves = tl === true || tl === 'true' || tl === 1 || tl === '1';
            if (!verRevPublicar && !tenemosLlaves) return null;
            return (
              <div style={{
                display: 'grid',
                gridTemplateColumns: (verRevPublicar && tenemosLlaves) ? '1fr 1fr' : '1fr',
                gap: 8, marginBottom: 12, alignItems: 'stretch',
              }}>
                {verRevPublicar && (
                  <div style={{ padding: '8px 10px', borderRadius: 10, border: `1px solid ${c.border}`, background: isDark ? 'rgba(139,92,246,0.08)' : '#f5f3ff', opacity: modRevPublicar ? 1 : 0.7, display: 'flex', alignItems: 'center' }} title={!modRevPublicar ? tituloSinPermisoRev : undefined}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: modRevPublicar ? 'pointer' : 'not-allowed', fontSize: 12, fontWeight: 600, color: c.textPrimary, minWidth: 0 }}>
                      <input
                        type="checkbox"
                        checked={publicarWebChecked}
                        disabled={!modRevPublicar}
                        onChange={(e) => {
                          if (!modRevPublicar) return;
                          if (e.target.checked) {
                            setModalPortalesAltaOpen(true);
                            return;
                          }
                          setValoresExtras((prev) => ({
                            ...prev,
                            publicar_web: false,
                            portales_alta_slugs: [],
                            portales_alta_pendiente: false,
                          }));
                          api.patch(`/captaciones/${captacion.id}/datos-extras`, {
                            datos: {
                              publicar_web: false,
                              portales_alta_slugs: [],
                              portales_alta_pendiente: false,
                            },
                            solo_borrador: true,
                          }).catch(() => {});
                        }}
                        style={{ width: 16, height: 16, accentColor: ml.purple, cursor: modRevPublicar ? 'pointer' : 'not-allowed', flexShrink: 0 }}
                      />
                      <span
                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: modRevPublicar && publicarWebChecked ? 'pointer' : undefined }}
                        onClick={(ev) => {
                          if (!modRevPublicar || !publicarWebChecked) return;
                          ev.preventDefault();
                          setModalPortalesAltaOpen(true);
                        }}
                        title={publicarWebChecked ? 'Clic para editar portales' : undefined}
                      >
                        Publicaciones
                        {(() => {
                          const slugs = Array.isArray(valoresExtras.portales_alta_slugs)
                            ? valoresExtras.portales_alta_slugs
                            : (Array.isArray(extrasCap.portales_alta_slugs) ? extrasCap.portales_alta_slugs : []);
                          if (!publicarWebChecked) return null;
                          if (!slugs.length) {
                            return <span style={{ fontWeight: 500, color: c.textMuted }}> · solo web</span>;
                          }
                          return <span style={{ fontWeight: 500, color: c.textMuted }}> · {slugs.length} portal{slugs.length === 1 ? '' : 'es'}</span>;
                        })()}
                      </span>
                    </label>
                  </div>
                )}
                {tenemosLlaves && (
                  <div style={{
                    padding: '8px 10px', borderRadius: 10,
                    border: `1px solid ${isDark ? 'rgba(34,197,94,0.35)' : '#bbf7d0'}`,
                    background: isDark ? 'rgba(34,197,94,0.08)' : '#f0fdf4',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                      <span style={{ fontSize: 10, fontWeight: 800, color: isDark ? '#4ade80' : '#15803d', letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                        🔑 Llaves
                      </span>
                      <input
                        type="text"
                        value={valoresExtras.num_llave ?? extrasCap.num_llave ?? ''}
                        onChange={(e) => setValoresExtras((prev) => ({ ...prev, num_llave: e.target.value }))}
                        onBlur={async (e) => {
                          e.target.style.borderColor = c.border;
                          const v = String(e.target.value || '').trim();
                          try {
                            await api.patch(`/captaciones/${captacion.id}/datos-extras`, {
                              datos: { num_llave: v || null, tenemos_llaves: true },
                              solo_borrador: true,
                            });
                          } catch (_) { /* noop */ }
                        }}
                        placeholder="Nº de llave *"
                        required
                        style={{ ...inputStyle, flex: 1, minWidth: 0, padding: '5px 8px', fontSize: 12 }}
                        onFocus={(e) => { e.target.style.borderColor = ml.purple; }}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Campos obligatorios faltantes — con inputs inline */}
          {camposObligatoriosVisibles.length > 0 && (
            <div style={{ padding: '0 0 8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                {verRevCamposObl && (
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#f59e0b' }}>
                  ⚠️ Campos obligatorios pendientes ({camposObligatorios.filter((c) => {
                    if (c.campo_key === 'precio_venta' || c.campo_key === 'fotos' || c.campo_key === 'publicar_web' || c.campo_key === 'visible_web') return false;
                    return !(CAMPOS_EXCLUSIVA_DEP.includes(c.campo_key) && !exclusivaActiva);
                  }).length})
                </div>
                )}
                {verRevBorrador && (
                <button onClick={handleGuardarExtras} disabled={guardandoExtras} style={{
                  padding: '5px 14px', borderRadius: 7, border: 'none', fontSize: 11, fontWeight: 700,
                  background: guardandoExtras ? c.bgInput : 'linear-gradient(135deg, #d97706, #f59e0b)',
                  color: guardandoExtras ? c.textMuted : '#fef3c7',
                  cursor: guardandoExtras ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                  boxShadow: guardandoExtras ? 'none' : '0 3px 10px rgba(245,158,11,0.3)',
                  opacity: guardandoExtras ? 0.6 : 1,
                }}>
                  {guardandoExtras ? 'Guardando...' : '💾 Guardar borrador'}
                </button>
                )}
              </div>
              {verRevCamposObl && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9, opacity: puedeEditarCamposAgente ? 1 : 0.65, pointerEvents: puedeEditarCamposAgente ? 'auto' : 'none' }} title={!puedeEditarCamposAgente ? tituloSinPermisoRev : undefined}>
                {camposOrdenados.map(campo => {
                  const tipo = getTipoCampo(campo.campo_key, campo.tipo_campo);
                  const key = campo.campo_key;
                  const val = valoresExtras[campo.campo_key] ?? '';
                  const setVal = (v) => setValoresExtras(prev => ({ ...prev, [campo.campo_key]: v }));
                  const baseInput = {
                    width: '100%', padding: '8px 11px', fontSize: 13,
                    border: `1px solid ${isDark ? 'rgba(245,158,11,0.4)' : '#fcd34d'}`, borderRadius: 7,
                    background: c.bgInput, color: c.textPrimary, outline: 'none',
                    fontFamily: 'inherit', boxSizing: 'border-box', marginTop: 5,
                    transition: 'border-color 0.15s',
                  };
                  let customInput = null;
                  if (key === 'provincia_id' || key === 'provincia') {
                    customInput = (
                      <select
                        value={val}
                        onChange={(e) => {
                          const v = e.target.value;
                          setVal(v);
                          if (key === 'provincia_id') setValoresExtras(prev => ({ ...prev, poblacion_id: '' }));
                          if (key === 'provincia') setValoresExtras(prev => ({ ...prev, poblacion: '' }));
                        }}
                        style={{ ...baseInput, cursor: 'pointer' }}
                      >
                        <option value="">Seleccionar provincia</option>
                        {PROVINCIAS_ES.map((p) => (
                          <option key={p.id} value={key === 'provincia_id' ? p.id : p.nombre}>{p.nombre}</option>
                        ))}
                      </select>
                    );
                  } else if (key === 'agente_id' || key === 'agente_responsable') {
                    customInput = (
                      <select value={val} onChange={(e) => setVal(e.target.value)} style={{ ...baseInput, cursor: 'pointer' }}>
                        <option value="">Seleccionar agente</option>
                        {agentes.map((a) => (
                          <option key={a.id} value={key === 'agente_id' ? a.id : a.nombre}>{a.nombre}</option>
                        ))}
                      </select>
                    );
                  } else if (key === 'poblacion_id' || key === 'poblacion') {
                    customInput = (
                      <select value={val} onChange={(e) => setVal(e.target.value)} style={{ ...baseInput, cursor: 'pointer' }}>
                        <option value="">{provinciaIdActual ? 'Seleccionar población' : 'Primero selecciona provincia'}</option>
                        {poblaciones.map((p) => (
                          <option key={p.id} value={key === 'poblacion_id' ? p.id : p.nombre}>{p.nombre}</option>
                        ))}
                      </select>
                    );
                  } else if (key === 'tipo_via_id' || key === 'tipo_via') {
                    const rows = tiposVia;
                    const selected = normalizeAuxSelectValue(val, rows);
                    customInput = (
                      <select
                        value={selected}
                        onChange={(e) => {
                          const id = e.target.value;
                          setValoresExtras((prev) => ({ ...prev, ...buildAuxExtrasPatch(key, id) }));
                        }}
                        style={{ ...baseInput, cursor: 'pointer' }}
                      >
                        <option value="">Seleccionar tipo de vía</option>
                        {rows.map((tv) => (
                          <option key={tv.id} value={tv.id}>{tv.nombre}</option>
                        ))}
                      </select>
                    );
                  } else if (key === 'tipo_inmueble_id' || key === 'tipo_inmueble') {
                    const rows = tiposInmueble;
                    const selected = normalizeAuxSelectValue(val, rows);
                    customInput = (
                      <select
                        value={selected}
                        onChange={(e) => {
                          const id = e.target.value;
                          setValoresExtras((prev) => ({ ...prev, ...buildAuxExtrasPatch(key, id) }));
                        }}
                        style={{ ...baseInput, cursor: 'pointer' }}
                      >
                        <option value="">Seleccionar tipo de inmueble</option>
                        {rows.map((ti) => (
                          <option key={ti.id} value={ti.id}>{ti.nombre}</option>
                        ))}
                      </select>
                    );
                  } else if (key === 'honorarios_porcentaje') {
                    customInput = (
                      <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          <div>
                            <div style={{ fontSize: 10, color: c.textMuted, marginBottom: 4 }}>Honorarios %</div>
                            <input type="number" step="0.01" value={val}
                              onChange={(e) => setVal(e.target.value)}
                              style={baseInput} />
                          </div>
                          <div>
                            <div style={{ fontSize: 10, color: c.textMuted, marginBottom: 4 }}>Precio propietario €</div>
                            <input type="text" inputMode="numeric" value={formatMoney(valoresExtras.precio_propietario)}
                              onChange={(e) => setValoresExtras(prev => ({ ...prev, precio_propietario: e.target.value }))}
                              style={baseInput} />
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button type="button" onClick={calcHonDesdeVentaYProp} style={{
                            padding: '7px 10px', borderRadius: 7, border: `1px solid ${c.accentGreen || '#22c55e'}`,
                            background: isDark ? '#0f1f16' : '#f0fdf4', color: c.accentGreen || '#22c55e',
                            fontSize: 11, fontWeight: 700, cursor: 'pointer',
                          }}>↕ Desde P.Venta − P.Propietario</button>
                          <button type="button" onClick={calcHonDesdeVentaYPct} style={{
                            padding: '7px 10px', borderRadius: 7, border: `1px solid ${c.accentGreen || '#22c55e'}`,
                            background: isDark ? '#0f1f16' : '#f0fdf4', color: c.accentGreen || '#22c55e',
                            fontSize: 11, fontWeight: 700, cursor: 'pointer',
                          }}>% Desde P.Venta + % Honorarios</button>
                        </div>
                        <div style={{
                          marginTop: 8,
                          padding: '8px 10px',
                          borderRadius: 7,
                          background: isDark ? 'rgba(34,197,94,0.08)' : '#f0fdf4',
                          border: `1px solid ${isDark ? 'rgba(34,197,94,0.25)' : '#bbf7d0'}`,
                          fontSize: 11,
                          color: c.textMuted,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4,
                        }}>
                          <div><span style={{ fontWeight: 700, color: c.textPrimary }}>Honorarios (base, sin IVA):</span>{' '}
                            {formatEuroConSimbolo(valoresExtras.honorarios_euros) || '—'}</div>
                          <div><span style={{ fontWeight: 700, color: c.textPrimary }}>IVA estimado:</span>{' '}
                            {formatEuroConSimbolo(valoresExtras.iva_euros) || '—'}</div>
                          <div><span style={{ fontWeight: 700, color: c.textPrimary }}>Total comisión (base + IVA):</span>{' '}
                            {formatEuroConSimbolo(valoresExtras.total_honorarios) || '—'}</div>
                        </div>
                      </div>
                    );
                  } else if (key === 'operacion') {
                    customInput = (
                      <select value={val} onChange={(e) => setVal(e.target.value)} style={{ ...baseInput, cursor: 'pointer' }}>
                        <option value="">Seleccionar operación</option>
                        {OPCIONES_OPERACION.map((op) => (
                          <option key={op} value={op}>{op}</option>
                        ))}
                      </select>
                    );
                  } else if (isVistasMultiCampo(key)) {
                    const rows = auxLists.vistas || [];
                    const selectedIds = normalizeVistasIdsFromExtras({ ...extrasCap, ...valoresExtras, ub_vistas_ids: valoresExtras.ub_vistas_ids, ub_vistas_id: val });
                    customInput = (
                      <MultiSelectAuxVistas
                        rows={rows}
                        selectedIds={selectedIds}
                        onChange={(ids) => setValoresExtras((prev) => ({ ...prev, ...buildVistasExtrasPatch(ids) }))}
                      />
                    );
                  } else {
                    const auxSlug = getAuxSlugForCampoKey(key);
                    if (auxSlug) {
                      const rowsRaw = auxLists[auxSlug] || [];
                      const rows = (auxSlug === 'aire-acondicionado' || auxSlug === 'calefaccion')
                        ? ordenarOpcionesTipoEquipoDesplegable(rowsRaw)
                        : rowsRaw;
                      // Ampliar: eq_aire/eq_calefaccion como select → usar *_id (no el boolean false)
                      const detailKey = (key === 'eq_aire' || key === 'eq_calefaccion'
                        || key === 'eq_piscina_priv' || key === 'eq_piscina_comun')
                        ? `${key}_id`
                        : (key.endsWith('_id') ? key : null);
                      const valSelect = detailKey
                        ? (valoresExtras[detailKey] ?? extrasCap[detailKey] ?? (typeof val === 'string' && /-/.test(val) ? val : '') ?? '')
                        : val;
                      const selected = normalizeAuxSelectValue(valSelect, rows);
                      customInput = (
                        <select
                          value={selected}
                          onChange={(e) => {
                            const id = e.target.value;
                            const patch = buildAuxExtrasPatch(key, id);
                            if (key === 'eq_aire' || key === 'eq_calefaccion' || key === 'eq_aire_id' || key === 'eq_calefaccion_id'
                              || key === 'eq_piscina_priv' || key === 'eq_piscina_comun'
                              || key === 'eq_piscina_priv_id' || key === 'eq_piscina_comun_id') {
                              const padre = key.endsWith('_id') ? key.slice(0, -3) : key;
                              const row = rows.find((r) => String(r.id) === String(id));
                              const nom = String(row?.nombre || '')
                                .toLowerCase()
                                .normalize('NFD')
                                .replace(/\p{M}/gu, '')
                                .trim();
                              const ausente = !id || !nom
                                || /^(no tiene|ninguno|ninguna|sin|n\/a|-)$/.test(nom)
                                || /^no tiene\b/.test(nom)
                                || /^sin\s+(aire|aa|a\.?\s*a\.?|calefaccion|calef\.?|piscina)/.test(nom);
                              patch[padre] = !ausente;
                              if (ausente) {
                                patch[`${padre}_id`] = '';
                                patch[padre] = false;
                              }
                            }
                            setValoresExtras((prev) => ({ ...prev, ...patch }));
                          }}
                          style={{ ...baseInput, cursor: 'pointer' }}
                        >
                          <option value="">Seleccionar…</option>
                          {rows.map((row) => (
                            <option key={row.id} value={row.id}>{row.nombre}</option>
                          ))}
                        </select>
                      );
                    }
                  }
                  return (
                    <div key={campo.campo_key} style={{
                      padding: '10px 12px', borderRadius: 9,
                      background: isDark ? 'rgba(245,158,11,0.06)' : '#fffbeb',
                      border: `1px solid ${isDark ? 'rgba(245,158,11,0.2)' : '#fde68a'}`,
                    }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#d97706', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {campo.campo_label}
                        {campo.responsable && (
                          <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 600, color: c.textMuted, textTransform: 'none' }}>
                            ({campo.responsable.join(', ')})
                          </span>
                        )}
                      </div>
                      {customInput ? customInput : tipo === 'boolean' ? (
                        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                          {[
                            { label: 'Sí', value: true, on: val === true || val === 'true' },
                            { label: 'No', value: false, on: val === false || val === 'false' },
                          ].map((opt) => (
                            <button
                              key={opt.label}
                              type="button"
                              onClick={() => setVal(opt.on ? null : opt.value)}
                              title={opt.on ? 'Clic para desmarcar' : undefined}
                              style={{
                                padding: '6px 14px',
                                borderRadius: 8,
                                fontSize: 13,
                                fontWeight: 700,
                                cursor: 'pointer',
                                border: `1px solid ${opt.on ? '#f59e0b' : (isDark ? 'rgba(245,158,11,0.4)' : '#fcd34d')}`,
                                background: opt.on ? (isDark ? 'rgba(245,158,11,0.18)' : '#fff7ed') : 'transparent',
                                color: opt.on ? '#f59e0b' : c.textMuted,
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      ) : tipo === 'textarea' ? (
                        <textarea value={val} onChange={e => setVal(e.target.value)} rows={2}
                          style={{ ...baseInput, resize: 'vertical', lineHeight: 1.5 }}
                          onFocus={e => e.target.style.borderColor = '#f59e0b'}
                          onBlur={e => e.target.style.borderColor = isDark ? 'rgba(245,158,11,0.4)' : '#fcd34d'} />
                      ) : isMoneyField(key) ? (
                        <input type="text" inputMode="numeric" placeholder="0 €"
                          value={formatMoney(val)} onChange={e => setVal(e.target.value)}
                          style={baseInput}
                          onFocus={e => e.target.style.borderColor = '#f59e0b'}
                          onBlur={e => e.target.style.borderColor = isDark ? 'rgba(245,158,11,0.4)' : '#fcd34d'} />
                      ) : (
                        <input type={tipo === 'number' ? 'number' : tipo === 'date' ? 'date' : 'text'}
                          value={tipo === 'date' && key === 'disponible_desde' && !val ? hoyISO() : val}
                          onChange={e => setVal(e.target.value)}
                          style={baseInput}
                          onFocus={e => e.target.style.borderColor = '#f59e0b'}
                          onBlur={e => e.target.style.borderColor = isDark ? 'rgba(245,158,11,0.4)' : '#fcd34d'} />
                      )}
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          )}

              </div>

          {verRevTextosWeb && (
          <div style={{
            marginTop: 8, marginBottom: 8, padding: '10px 12px', borderRadius: 10,
            border: `1px solid ${isDark ? 'rgba(139,92,246,0.35)' : '#ddd6fe'}`,
            background: isDark ? 'rgba(139,92,246,0.08)' : '#faf5ff',
            flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
            opacity: modRevTextosWeb ? 1 : 0.75,
          }} title={!modRevTextosWeb ? tituloSinPermisoRev : undefined}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
              <span style={{ ...labelStyle, marginBottom: 0, color: ml.labelAccent }}>Textos para web (secretaría)</span>
              {(textosWebPendientes || generandoDesc) && (
                <span style={{ fontSize: 10, color: ml.labelAccent, fontWeight: 600 }}>
                  {generandoDesc ? 'Regenerando…' : 'Generando al pasar a Para Revisar…'}
                </span>
              )}
            </div>
            <label style={labelStyle}>Título</label>
            <input
              type="text"
              value={
                tituloCaptacion({ ...extrasCap, ...valoresExtras })
                || ''
              }
              disabled={!modRevTextosWeb}
              onChange={(e) => {
                const v = e.target.value;
                setValoresExtras((prev) => ({ ...prev, titulo: v, web_titulo: v }));
              }}
              placeholder="Título para publicación web"
              style={{ ...inputStyle, marginBottom: 8 }}
              onFocus={(e) => { e.target.style.borderColor = ml.purple; }}
              onBlur={(e) => {
                e.target.style.borderColor = c.border;
                persistTextosWebSiCambiaron();
              }}
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Descripción larga</label>
              <button
                type="button"
                onClick={() => generarDescripcionCap('larga', { forzar: true })}
                disabled={!!generandoDesc || !modRevTextosWeb}
                style={{
                  padding: '2px 8px', borderRadius: 6, border: `1px solid ${c.border}`,
                  background: c.bgInput, color: ml.labelAccent, fontSize: 10, fontWeight: 700,
                  cursor: (generandoDesc || !modRevTextosWeb) ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: generandoDesc ? 0.5 : 1,
                }}
              >🤖 Regenerar</button>
            </div>
            <textarea
              value={
                Object.prototype.hasOwnProperty.call(valoresExtras, 'web_desc_larga_es')
                  ? (valoresExtras.web_desc_larga_es ?? '')
                  : (capFresh ? String(parseExtrasCap(capFresh).web_desc_larga_es || '') : '')
              }
              disabled={!modRevTextosWeb}
              onChange={(e) => setValoresExtras((prev) => ({ ...prev, web_desc_larga_es: e.target.value }))}
              rows={6}
              placeholder={capFresh ? 'Descripción detallada para la web…' : 'Cargando descripción…'}
              style={{ ...inputStyle, resize: 'none', lineHeight: 1.4, minHeight: 110, flex: 1 }}
              onFocus={(e) => { e.target.style.borderColor = ml.purple; }}
              onBlur={(e) => {
                e.target.style.borderColor = c.border;
                persistTextosWebSiCambiaron();
              }}
            />
          </div>
          )}

              {verRevConfSec && (
              <div style={{ marginTop: 8, flexShrink: 0 }}>
                <button
                  type="button"
                  onClick={handleConformeSecretaria}
                  disabled={guardandoSecretaria || yaConformeSecretaria}
                  style={{
                    width: '100%', padding: '12px 14px', borderRadius: 10, border: 'none',
                    cursor: (guardandoSecretaria || yaConformeSecretaria) ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit', fontSize: 13, fontWeight: 700, color: '#fff',
                    background: yaConformeSecretaria
                      ? (isDark ? '#14532d' : '#22c55e')
                      : 'linear-gradient(135deg, #0284c7, #0369a1)',
                    boxShadow: yaConformeSecretaria ? 'none' : '0 4px 12px rgba(2,132,199,0.35)',
                    opacity: guardandoSecretaria ? 0.6 : 1,
                  }}
                >{guardandoSecretaria ? 'Registrando…' : yaConformeSecretaria ? '✓ Conforme secretaría' : 'Conforme secretaría'}</button>
                {labelConformeSecretaria && (
                  <div style={{
                    marginTop: 10, fontSize: 12, fontWeight: 600, color: '#22c55e', padding: '10px 12px', borderRadius: 8,
                    background: isDark ? 'rgba(34,197,94,0.12)' : '#f0fdf4',
                    border: `1px solid ${isDark ? 'rgba(34,197,94,0.3)' : '#bbf7d0'}`,
                  }}>{labelConformeSecretaria}</div>
                )}
              </div>
              )}
            </div>

            <div style={{
              padding: '4px 4px 12px 12px',
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 0,
              height: '100%',
            }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: ml.labelAccent, marginBottom: 6, flexShrink: 0 }}>Director</div>

              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', paddingRight: 2 }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: ml.labelAccent, marginBottom: 5 }}>
                Fotos
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                {(() => {
                  const lista = esOrigenMovil ? fotosDirectorConsulta : fotosReportaje;
                  const n = lista.length;
                  return (
                    <button
                      type="button"
                      title={esOrigenMovil ? 'Fotos seleccionadas (solo consulta)' : 'Fotos del inmueble (solo consulta)'}
                      disabled={!n}
                      onClick={() => {
                        if (!n) return;
                        abrirLightboxFoto(0, true, esOrigenMovil ? 'seleccionadas' : 'todas');
                      }}
                      style={{
                        position: 'relative',
                        flex: '1 1 140px', height: ALTO_TARJETA_FOTOS, borderRadius: 12, cursor: n ? 'pointer' : 'not-allowed', fontFamily: 'inherit',
                        border: `2px solid ${c.border}`,
                        background: isDark ? c.bgInput : '#fff',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
                        color: c.text, padding: '8px 10px', opacity: n ? 1 : 0.5, overflow: 'hidden',
                      }}
                    >
                      {esOrigenMovil && n > 0 && (
                        <span style={{ position: 'absolute', top: 4, right: 6, fontSize: 8, fontWeight: 800, color: ml.labelAccent, letterSpacing: '0.03em' }}>
                          {prioridadSeleccion === 'marco' ? 'MARCO' : 'TODAS'}
                        </span>
                      )}
                      <span style={{
                        width: 36, height: 36, borderRadius: 9,
                        background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 18, flexShrink: 0,
                        boxShadow: '0 4px 12px rgba(124,58,237,0.35)',
                      }}>🖼️</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: isDark ? '#fff' : '#0f172a', lineHeight: 1.15 }}>
                        {esOrigenMovil ? 'Fotos seleccionadas' : 'Fotos del inmueble'}
                      </span>
                      <span style={{ fontSize: 15, fontWeight: 800, color: isDark ? '#fff' : '#0f172a', letterSpacing: '0.02em', lineHeight: 1.15 }}>
                        {n ? `${n} fotos` : 'Sin fotos'}
                      </span>
                    </button>
                  );
                })()}
              </div>
              {videosReportaje.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                  {videosReportaje.map((v) => (
                    <VideoThumbButton
                      key={`dir-v-${v.id}`}
                      videoUrl={v.url}
                      thumbUrl={v.url_thumb}
                      border={c.border}
                      width={72}
                      height={52}
                      playFontSize={9}
                      onClick={() => {
                        setVisorVideoError(null);
                        setVisorVideoUrl(absolutizarMediaUrl(v.url) || '__pending__');
                      }}
                    />
                  ))}
                </div>
              )}
              {docsDocumentacionUi.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  {docsDocumentacionUi.map((doc) => renderFilaDocumento(doc, { keyPrefix: 'dir', compact: true }))}
                </div>
              )}

              {verRevPrecioHon && (
              <div style={{ marginTop: 4, opacity: modRevPrecioHon ? 1 : 0.7 }} title={!modRevPrecioHon ? tituloSinPermisoRev : undefined}>
                <label style={labelStyle}>Precio de venta *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={precioVentaStr}
                    disabled={!modRevPrecioHon}
                    onChange={(e) => setPrecioVentaStr(e.target.value)}
                    onBlur={(e) => {
                      e.target.style.borderColor = c.border;
                      const n = parsePrecioVentaEntero(precioVentaStr);
                      setPrecioVentaStr(Number.isFinite(n) && n >= 0 ? formatPrecioVentaEntero(n) : precioVentaStr);
                    }}
                    placeholder="0"
                    style={{ ...inputStyle, paddingRight: 36 }}
                    onFocus={(e) => { e.target.style.borderColor = ml.purple; }}
                  />
                  <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: c.textMuted, fontSize: 13, fontWeight: 600, pointerEvents: 'none' }}>€</span>
                </div>
                <div style={{ fontSize: 10, color: c.textMuted, marginTop: 4 }}>Euros enteros (sin céntimos). Ej. 88.000 o 88000.</div>
              </div>
              )}

              {verRevPrecioHon && (
              <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 10, border: `1px solid ${c.border}`, background: isDark ? 'rgba(139,92,246,0.06)' : '#faf5ff', opacity: modRevPrecioHon ? 1 : 0.7 }} title={!modRevPrecioHon ? tituloSinPermisoRev : undefined}>
                <label style={{ ...labelStyle, marginBottom: 6 }}>Honorarios (director)</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 10, color: c.textMuted, marginBottom: 4 }}>Honorarios %</div>
                    <input type="number" step="0.01" value={valoresExtras.honorarios_porcentaje ?? ''}
                      disabled={!modRevPrecioHon}
                      onChange={(e) => setValoresExtras(prev => ({ ...prev, honorarios_porcentaje: e.target.value }))}
                      style={inputStyle} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, color: c.textMuted, marginBottom: 4 }}>Precio propietario €</div>
                    <input type="text" inputMode="numeric" value={formatMoney(valoresExtras.precio_propietario)}
                      disabled={!modRevPrecioHon}
                      onChange={(e) => setValoresExtras(prev => ({ ...prev, precio_propietario: e.target.value }))}
                      style={inputStyle} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  <button type="button" onClick={calcHonDesdeVentaYProp} disabled={!modRevPrecioHon} style={{
                    padding: '7px 10px', borderRadius: 7, border: `1px solid ${c.accentGreen || '#22c55e'}`,
                    background: isDark ? '#0f1f16' : '#f0fdf4', color: c.accentGreen || '#22c55e',
                    fontSize: 11, fontWeight: 700, cursor: modRevPrecioHon ? 'pointer' : 'not-allowed',
                  }}>↕ Desde P.Venta − P.Propietario</button>
                  <button type="button" onClick={calcHonDesdeVentaYPct} disabled={!modRevPrecioHon} style={{
                    padding: '7px 10px', borderRadius: 7, border: `1px solid ${c.accentGreen || '#22c55e'}`,
                    background: isDark ? '#0f1f16' : '#f0fdf4', color: c.accentGreen || '#22c55e',
                    fontSize: 11, fontWeight: 700, cursor: modRevPrecioHon ? 'pointer' : 'not-allowed',
                  }}>% Desde P.Venta + % Honorarios</button>
                </div>
                <div style={{ marginTop: 8, fontSize: 11, color: c.textMuted, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div><span style={{ fontWeight: 700, color: c.textPrimary }}>Honorarios (base, sin IVA):</span>{' '}
                    {formatEuroConSimbolo(valoresExtras.honorarios_euros) || '—'}</div>
                  <div><span style={{ fontWeight: 700, color: c.textPrimary }}>IVA estimado:</span>{' '}
                    {formatEuroConSimbolo(valoresExtras.iva_euros) || '—'}</div>
                  <div><span style={{ fontWeight: 700, color: c.textPrimary }}>Total comisión (base + IVA):</span>{' '}
                    {formatEuroConSimbolo(valoresExtras.total_honorarios) || '—'}</div>
                </div>
                {refManual && (
                  <div style={{ marginTop: 8, fontSize: 11, color: c.accentBlue, fontWeight: 600 }}>
                    Referencia manual al validar: {refManual}
                  </div>
                )}
              </div>
              )}

              {verRevCalificacion && (
              <div style={{ padding: '8px 0 4px', opacity: modRevCalificacion ? 1 : 0.7 }} title={!modRevCalificacion ? tituloSinPermisoRev : undefined}>
                <label style={{ ...labelStyle, marginBottom: 4 }}>Calificación *</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  {CALIFICACIONES.map(cal => (
                    <button key={cal.valor} type="button" onClick={() => { if (modRevCalificacion) setCalificacion(cal.valor); }} disabled={!modRevCalificacion} style={{
                      flex: 1, padding: '10px 4px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                      border: `2px solid ${calificacion === cal.valor ? cal.color : c.border}`,
                      background: calificacion === cal.valor ? `${cal.color}18` : c.bgInput,
                      color: calificacion === cal.valor ? cal.color : c.textMuted,
                      cursor: modRevCalificacion ? 'pointer' : 'not-allowed', fontFamily: 'inherit', transition: 'all 0.15s',
                    }}>{cal.label}</button>
                  ))}
                </div>
              </div>
              )}
              </div>

              {verRevConfDir && (
              <div style={{
                flexShrink: 0,
                paddingTop: 10,
                borderTop: `1px solid ${isDark ? c.border : '#a8a8a0'}`,
                background: isDark ? c.bgCard : '#c8c8be',
              }}>
              <button type="button" onClick={handleConformeDirector} disabled={guardandoDirector || yaConformeDirector} style={{
                width: '100%', padding: '12px 18px', borderRadius: 10, border: 'none',
                background: yaConformeDirector
                  ? (isDark ? '#14532d' : '#22c55e')
                  : (guardandoDirector ? c.bgInput : `linear-gradient(135deg, ${ml.purpleFrom}, ${ml.purpleTo})`),
                color: yaConformeDirector ? '#fff' : (guardandoDirector ? c.textMuted : ml.purpleText),
                cursor: (guardandoDirector || yaConformeDirector) ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                boxShadow: (guardandoDirector || yaConformeDirector) ? 'none' : `0 4px 14px ${ml.purpleShadow}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                opacity: guardandoDirector ? 0.5 : 1,
              }}>
                <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
                </svg>
                {guardandoDirector ? 'Registrando…' : yaConformeDirector ? '✓ Conforme director' : 'Conforme director'}
              </button>
              {labelConformeDirector && (
                <div style={{
                  marginTop: 10, fontSize: 12, fontWeight: 600, color: '#22c55e', padding: '10px 12px', borderRadius: 8,
                  background: isDark ? 'rgba(34,197,94,0.12)' : '#f0fdf4',
                  border: `1px solid ${isDark ? 'rgba(34,197,94,0.3)' : '#bbf7d0'}`,
                }}>{labelConformeDirector}</div>
              )}
              </div>
              )}
            </div>
          </div>

          {/* Guardado OK / conforme parcial */}
          {(guardadoExtrasOk || msgConforme) && (
            <div style={{ padding: '12px 24px 0' }}>
              <div style={{
                padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                background: isDark ? '#0d2818' : '#f0fdf4',
                border: `1px solid ${isDark ? '#166534' : '#bbf7d0'}`,
                color: '#22c55e',
              }}>{msgConforme || '✓ Campos guardados correctamente'}</div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ padding: '12px 24px 0' }}>
              <div style={{
                padding: '10px 14px', borderRadius: 8, fontSize: 13,
                background: isDark ? '#2a1215' : '#fef2f2',
                border: `1px solid ${isDark ? '#5c1d1d' : '#fecaca'}`,
                color: '#ef4444',
              }}>{error}</div>
            </div>
          )}

          <div style={{ height: 8 }} />
        </div>

        {lightboxFotoIdx !== null && fotosLightboxLista[lightboxFotoIdx] && createPortal(
          <div
            role="presentation"
            onClick={cerrarLightboxFoto}
            style={{
              position: 'fixed', inset: 0, zIndex: 12000, background: 'rgba(0,0,0,0.92)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute', top: 16, right: 16, display: 'flex', gap: 8, zIndex: 2,
              }}
            >
              {modRevFotosSec && !lightboxFotoSoloConsulta && lightboxModoFotos === 'todas' && (
                <button
                  type="button"
                  title="Insertar más fotos"
                  onClick={() => inputFotoRef.current?.click()}
                  disabled={subiendoFotos}
                  style={{
                    padding: '8px 12px', borderRadius: 8, border: 'none',
                    cursor: subiendoFotos ? 'wait' : 'pointer',
                    background: '#8b5cf6', color: '#fff', fontWeight: 700, fontSize: 12, fontFamily: 'inherit',
                  }}
                >
                  {subiendoFotos ? 'Subiendo…' : '+ Insertar fotos'}
                </button>
              )}
              {esOrigenMovil && !lightboxFotoSoloConsulta && lightboxModoFotos === 'marco' && (
                <button
                  type="button"
                  title="Insertar fotos en la selección Marco"
                  onClick={() => { setInsertarMarcoOpen(true); setInsertarPreviewIdx(null); }}
                  style={{
                    padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                    background: '#8b5cf6', color: '#fff', fontWeight: 700, fontSize: 12, fontFamily: 'inherit',
                  }}
                >
                  Insertar…
                </button>
              )}
              <button
                type="button"
                title={lightboxVista === 'pantalla' ? 'Ver parrilla (3 por fila)' : 'Ver pantalla completa'}
                onClick={() => {
                  setLightboxVista((v) => (v === 'pantalla' ? 'parrilla' : 'pantalla'));
                  setLightboxParrillaSelIds([]);
                }}
                style={{
                  padding: '8px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: 'rgba(255,255,255,0.92)', color: '#111', fontWeight: 700, fontSize: 12, fontFamily: 'inherit',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                {lightboxVista === 'pantalla' ? (
                  <>▦ Parrilla</>
                ) : (
                  <>⛶ Pantalla</>
                )}
              </button>
              <button
                type="button"
                onClick={cerrarLightboxFoto}
                style={{
                  padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
                }}
              >Cerrar</button>
            </div>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute', top: 16, left: 16, display: 'flex', flexDirection: 'column', gap: 6, maxWidth: '55vw',
              }}
            >
              <span style={{ color: '#fff', fontSize: 12, fontWeight: 700, textShadow: '0 1px 4px #000' }}>
                {lightboxModoFotos === 'seleccionadas'
                  ? 'Fotos seleccionadas'
                  : lightboxModoFotos === 'marco'
                    ? 'Selección Marco'
                    : 'Todas las fotos'}
                {esOrigenMovil && !lightboxFotoSoloConsulta ? ` · Prioridad: ${prioridadSeleccion === 'marco' ? 'Marco' : 'Todas'}` : ''}
                {lightboxFotoSoloConsulta ? ' · Solo consulta' : ''}
              </span>
              {esOrigenMovil && !lightboxFotoSoloConsulta && (
                <button
                  type="button"
                  onClick={() => guardarPrioridadSeleccion(lightboxModoFotos === 'marco' ? 'marco' : 'todas')}
                  style={{
                    alignSelf: 'flex-start', padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                    fontWeight: 700, fontSize: 11, fontFamily: 'inherit',
                    background: (lightboxModoFotos === 'marco' ? prioridadSeleccion === 'marco' : prioridadSeleccion === 'todas')
                      ? '#8b5cf6' : 'rgba(255,255,255,0.92)',
                    color: (lightboxModoFotos === 'marco' ? prioridadSeleccion === 'marco' : prioridadSeleccion === 'todas')
                      ? '#fff' : '#111',
                  }}
                >
                  {(lightboxModoFotos === 'marco' ? prioridadSeleccion === 'marco' : prioridadSeleccion === 'todas')
                    ? '✓ Prioridad activa'
                    : 'Dar prioridad a estas'}
                </button>
              )}
              {esOrigenMovil && !lightboxFotoSoloConsulta && lightboxModoFotos === 'marco' && fotosLightboxLista.length > 0 && (
                <label
                  style={{
                    alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 8,
                    padding: '6px 10px', borderRadius: 8, cursor: 'pointer', userSelect: 'none',
                    background: 'rgba(255,255,255,0.92)', color: '#111', fontWeight: 700, fontSize: 11,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={fotosLightboxLista.every((f) => f.marco_autoenhance === false)}
                    onChange={(e) => setEdicionTodasSeleccionMarco(!e.target.checked)}
                    style={{ width: 15, height: 15, cursor: 'pointer' }}
                  />
                  Desactivar edición a todas
                </label>
              )}
            </div>

            {lightboxVista === 'parrilla' ? (
              <>
                <DndContext
                  sensors={lightboxParrillaSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(event) => {
                    if (lightboxFotoSoloConsulta) return;
                    const { active, over } = event;
                    if (!over || active.id === over.id) return;
                    const fromIdx = fotosLightboxLista.findIndex((f) => String(f.id) === String(active.id));
                    const toIdx = fotosLightboxLista.findIndex((f) => String(f.id) === String(over.id));
                    if (fromIdx < 0 || toIdx < 0) return;
                    moverFotoEnParrillaLightbox(fromIdx, toIdx);
                  }}
                >
                  <SortableContext
                    items={fotosLightboxLista.map((f, i) => String(f.id || `lb-${i}`))}
                    strategy={rectSortingStrategy}
                  >
                    <div
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        /* 3 cols ≈ mismo tamaño de celda que antes con 2 en ~960px → ~1440px */
                        width: 'min(1440px, 96vw)',
                        maxHeight: lightboxParrillaSelIds.length && esOrigenMovil && !lightboxFotoSoloConsulta
                          ? 'calc(82vh - 52px)' : '82vh',
                        overflowY: 'auto',
                        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, padding: '8px 4px',
                        alignContent: 'start',
                        marginBottom: lightboxParrillaSelIds.length && esOrigenMovil && !lightboxFotoSoloConsulta ? 56 : 0,
                      }}
                    >
                      {fotosLightboxLista.map((foto, idx) => {
                        const activa = idx === lightboxFotoIdx;
                        const seleccionada = foto.id && lightboxParrillaSelIds.includes(foto.id);
                        const puedeSeleccionar = esOrigenMovil && !lightboxFotoSoloConsulta && !!foto.id;
                        const puedeReordenar = !lightboxFotoSoloConsulta && !!foto.id;
                        const sortableId = String(foto.id || `lb-${idx}`);
                        return (
                          <LightboxParrillaSortableCell
                            key={`lb-grid-${sortableId}`}
                            id={sortableId}
                            disabled={!puedeReordenar}
                            style={{
                              position: 'relative',
                              border: seleccionada
                                ? '3px solid #22c55e'
                                : activa
                                  ? '3px solid #c084fc'
                                  : `1px solid ${isDark ? '#334155' : '#64748b'}`,
                              borderRadius: 10, overflow: 'hidden', background: '#0f172a',
                              aspectRatio: '4 / 3',
                              cursor: puedeReordenar ? 'grab' : 'pointer',
                              contentVisibility: 'auto',
                              containIntrinsicSize: '240px',
                            }}
                          >
                            {({ dragAttributes, dragListeners, isDragging }) => (
                              <>
                                {puedeSeleccionar && (
                                  <label
                                    onClick={(e) => e.stopPropagation()}
                                    onPointerDown={(e) => e.stopPropagation()}
                                    title={seleccionada ? 'Quitar de la selección en lote' : 'Seleccionar para lote'}
                                    style={{
                                      position: 'absolute', top: 8, left: 8, zIndex: 4,
                                      width: 30, height: 30, borderRadius: 6,
                                      background: seleccionada ? '#22c55e' : 'rgba(15,23,42,0.88)',
                                      border: '2px solid #fff',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.45)',
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={!!seleccionada}
                                      onChange={() => toggleLightboxParrillaSel(foto.id)}
                                      style={{ position: 'absolute', opacity: 0, width: 1, height: 1 }}
                                    />
                                    {seleccionada ? (
                                      <span style={{ color: '#fff', fontWeight: 900, fontSize: 16, lineHeight: 1 }}>✓</span>
                                    ) : (
                                      <span style={{
                                        width: 12, height: 12, borderRadius: 2,
                                        border: '2px solid rgba(255,255,255,0.85)', display: 'block',
                                      }}
                                      />
                                    )}
                                  </label>
                                )}
                                <div
                                  role="button"
                                  tabIndex={0}
                                  title={puedeReordenar ? 'Arrastra para ordenar · clic para ver en grande' : 'Clic para ver en grande'}
                                  {...dragAttributes}
                                  {...dragListeners}
                                  onClick={() => {
                                    if (isDragging) return;
                                    setLightboxFotoIdx(idx);
                                    setLightboxVista('pantalla');
                                    setLightboxParrillaSelIds([]);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key !== 'Enter' && e.key !== ' ') return;
                                    e.preventDefault();
                                    setLightboxFotoIdx(idx);
                                    setLightboxVista('pantalla');
                                    setLightboxParrillaSelIds([]);
                                  }}
                                  style={{
                                    display: 'block', width: '100%', height: '100%', padding: 0,
                                    border: 'none', cursor: puedeReordenar ? 'grab' : 'pointer', background: 'transparent',
                                  }}
                                >
                                  <CaptacionFotoThumb
                                    foto={foto}
                                    soloThumb
                                    style={{ width: '100%', height: '100%', pointerEvents: 'none' }}
                                    imgStyle={{ pointerEvents: 'none' }}
                                  />
                                </div>
                                <span style={{
                                  position: 'absolute', bottom: 6, left: 6, padding: '2px 7px', borderRadius: 6,
                                  fontSize: 11, fontWeight: 800, background: 'rgba(0,0,0,0.65)', color: '#fff',
                                  pointerEvents: 'none',
                                }}>
                                  {idx === 0 ? 'Portada' : `#${idx + 1}`}
                                </span>
                                {foto.id && (
                                  <button
                                    type="button"
                                    title="Ver metadatos de la foto"
                                    onClick={(e) => { e.stopPropagation(); abrirMetadatosFoto(foto); }}
                                    onPointerDown={(e) => e.stopPropagation()}
                                    style={{
                                      position: 'absolute', bottom: 6, right: esOrigenMovil && !lightboxFotoSoloConsulta ? 58 : 6,
                                      zIndex: 3, width: 28, height: 28, borderRadius: 8, border: 'none', cursor: 'pointer',
                                      fontSize: 13, fontWeight: 900, background: 'rgba(15,23,42,0.88)', color: '#e2e8f0',
                                      boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                                    }}
                                  >
                                    i
                                  </button>
                                )}
                                {esOrigenMovil && !lightboxFotoSoloConsulta && foto.usar_web === false && (
                                  <span style={{
                                    position: 'absolute', top: 8, right: 8, padding: '2px 6px', borderRadius: 6,
                                    fontSize: 9, fontWeight: 800, background: '#ef4444', color: '#fff',
                                    pointerEvents: 'none',
                                  }}>WEB OFF</span>
                                )}
                                {esOrigenMovil && !lightboxFotoSoloConsulta && foto.id && (
                                  <button
                                    type="button"
                                    title={foto.marco_autoenhance === false ? 'Activar edición Marco' : 'Desactivar edición Marco'}
                                    onClick={(e) => { e.stopPropagation(); toggleMarcoFoto(foto); }}
                                    onPointerDown={(e) => e.stopPropagation()}
                                    style={{
                                      position: 'absolute', bottom: 6, right: 6, zIndex: 2, padding: '2px 7px', borderRadius: 6,
                                      fontSize: 9, fontWeight: 800, border: 'none', cursor: 'pointer',
                                      background: foto.marco_autoenhance === false ? 'rgba(100,116,139,0.85)' : 'rgba(139,92,246,0.92)',
                                      color: '#fff',
                                    }}
                                  >
                                    ✎ {foto.marco_autoenhance === false ? 'OFF' : 'ON'}
                                  </button>
                                )}
                                {!lightboxFotoSoloConsulta && foto.id && (
                                  <div
                                    onPointerDown={(e) => e.stopPropagation()}
                                    style={{
                                      /* Si hay casilla de lote a la izquierda, girar a su derecha */
                                      position: 'absolute', top: 8, left: puedeSeleccionar ? 46 : 8, zIndex: 3,
                                      display: 'flex', gap: 4,
                                    }}
                                  >
                                    <button
                                      type="button"
                                      title="Girar antihorario"
                                      onClick={(e) => { e.stopPropagation(); rotarFotoReportaje(foto, -90); }}
                                      style={{
                                        padding: '4px 7px', borderRadius: 6, border: 'none', cursor: 'pointer',
                                        fontSize: 12, fontWeight: 800, background: 'rgba(15,23,42,0.8)', color: '#fff',
                                      }}
                                    >↺</button>
                                    <button
                                      type="button"
                                      title="Girar horario"
                                      onClick={(e) => { e.stopPropagation(); rotarFotoReportaje(foto, 90); }}
                                      style={{
                                        padding: '4px 7px', borderRadius: 6, border: 'none', cursor: 'pointer',
                                        fontSize: 12, fontWeight: 800, background: 'rgba(15,23,42,0.8)', color: '#fff',
                                      }}
                                    >↻</button>
                                  </div>
                                )}
                              </>
                            )}
                          </LightboxParrillaSortableCell>
                        );
                      })}
                    </div>
                  </SortableContext>
                </DndContext>
                {esOrigenMovil && !lightboxFotoSoloConsulta && lightboxParrillaSelIds.length > 0 && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
                      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'center',
                      padding: '10px 14px', borderRadius: 12,
                      background: 'rgba(15,23,42,0.92)', border: '1px solid rgba(255,255,255,0.2)',
                      maxWidth: '94vw', zIndex: 3,
                    }}
                  >
                    <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>
                      {lightboxParrillaSelIds.length} seleccionada{lightboxParrillaSelIds.length === 1 ? '' : 's'}
                    </span>
                    <button
                      type="button"
                      onClick={() => setLightboxParrillaSelIds([])}
                      style={{
                        padding: '7px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                        fontWeight: 700, fontSize: 12, fontFamily: 'inherit',
                        background: 'rgba(255,255,255,0.15)', color: '#fff',
                      }}
                    >
                      Limpiar
                    </button>
                    <button
                      type="button"
                      title="Girar antihorario"
                      onClick={() => rotarVariasFotosReportaje(lightboxParrillaSelIds, -90)}
                      style={{
                        padding: '7px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                        fontWeight: 700, fontSize: 12, fontFamily: 'inherit',
                        background: 'rgba(255,255,255,0.92)', color: '#111',
                      }}
                    >
                      ↺ Girar
                    </button>
                    <button
                      type="button"
                      title="Girar horario"
                      onClick={() => rotarVariasFotosReportaje(lightboxParrillaSelIds, 90)}
                      style={{
                        padding: '7px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                        fontWeight: 700, fontSize: 12, fontFamily: 'inherit',
                        background: 'rgba(255,255,255,0.92)', color: '#111',
                      }}
                    >
                      ↻ Girar
                    </button>
                    <button
                      type="button"
                      onClick={() => setEdicionVariasFotos(lightboxParrillaSelIds, true)}
                      style={{
                        padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                        fontWeight: 700, fontSize: 12, fontFamily: 'inherit',
                        background: '#8b5cf6', color: '#fff',
                      }}
                    >
                      ✎ Activar edición
                    </button>
                    <button
                      type="button"
                      onClick={() => setEdicionVariasFotos(lightboxParrillaSelIds, false)}
                      style={{
                        padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                        fontWeight: 700, fontSize: 12, fontFamily: 'inherit',
                        background: '#64748b', color: '#fff',
                      }}
                    >
                      ✎ Desactivar edición
                    </button>
                    {lightboxModoFotos === 'marco' ? (
                      <button
                        type="button"
                        onClick={() => quitarVariasDeSeleccionMarco(lightboxParrillaSelIds)}
                        style={{
                          padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                          fontWeight: 700, fontSize: 12, fontFamily: 'inherit',
                          background: '#f59e0b', color: '#fff',
                        }}
                      >
                        Quitar de la selección
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => excluirVariasDeWeb(lightboxParrillaSelIds)}
                        style={{
                          padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                          fontWeight: 700, fontSize: 12, fontFamily: 'inherit',
                          background: '#f59e0b', color: '#fff',
                        }}
                      >
                        Excluir de Web
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => eliminarVariasFotosReportaje(lightboxParrillaSelIds)}
                      style={{
                        padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                        fontWeight: 700, fontSize: 12, fontFamily: 'inherit',
                        background: '#ef4444', color: '#fff',
                      }}
                    >
                      🗑 Eliminar
                    </button>
                  </div>
                )}
                {!(esOrigenMovil && !lightboxFotoSoloConsulta && lightboxParrillaSelIds.length > 0) && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
                      color: '#fff', fontSize: 12, fontWeight: 600, textShadow: '0 1px 4px #000',
                    }}
                  >
                    {fotosLightboxLista.length} fotos
                    {!lightboxFotoSoloConsulta ? ' · arrastra para ordenar' : ''}
                    {' · clic para pantalla completa'}
                    {esOrigenMovil && !lightboxFotoSoloConsulta ? ' · checkbox para lote' : ''}
                  </div>
                )}
              </>
            ) : (
              <>
                {fotosLightboxLista.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setLightboxFotoIdx((i) => (i > 0 ? i - 1 : fotosLightboxLista.length - 1));
                      }}
                      style={{
                        position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)',
                        width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: 'pointer', fontSize: 22, color: '#111',
                        background: 'rgba(255,255,255,0.9)', fontWeight: 700,
                      }}
                    >‹</button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setLightboxFotoIdx((i) => (i < fotosLightboxLista.length - 1 ? i + 1 : 0));
                      }}
                      style={{
                        position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)',
                        width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: 'pointer', fontSize: 22, color: '#111',
                        background: 'rgba(255,255,255,0.9)', fontWeight: 700,
                      }}
                    >›</button>
                  </>
                )}
                <CaptacionFotoThumb
                  foto={fotosLightboxLista[lightboxFotoIdx]}
                  mode="full"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    maxWidth: '96vw',
                    maxHeight: '82vh',
                    minWidth: 320,
                    minHeight: 200,
                    borderRadius: 8,
                  }}
                  imgStyle={{ maxWidth: '96vw', maxHeight: '82vh', objectFit: 'contain', borderRadius: 8 }}
                />
                {(!lightboxFotoSoloConsulta) && fotosLightboxLista[lightboxFotoIdx]?.id && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute', bottom: 56, left: '50%', transform: 'translateX(-50%)',
                      display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center', maxWidth: '92vw',
                    }}
                  >
                    <button
                      type="button"
                      title="Girar antihorario"
                      onClick={() => rotarFotoReportaje(fotosLightboxLista[lightboxFotoIdx], -90)}
                      style={{
                        padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12,
                        background: 'rgba(255,255,255,0.92)', color: '#111',
                      }}
                    >
                      ↺ Girar
                    </button>
                    <button
                      type="button"
                      title="Girar horario"
                      onClick={() => rotarFotoReportaje(fotosLightboxLista[lightboxFotoIdx], 90)}
                      style={{
                        padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12,
                        background: 'rgba(255,255,255,0.92)', color: '#111',
                      }}
                    >
                      ↻ Girar
                    </button>
                    <button
                      type="button"
                      title="Ver metadatos de la foto"
                      onClick={() => abrirMetadatosFoto(fotosLightboxLista[lightboxFotoIdx])}
                      style={{
                        padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12,
                        background: 'rgba(14,165,233,0.95)', color: '#fff',
                      }}
                    >
                      i Metadatos
                    </button>
                    {esOrigenMovil && (
                      <>
                    <button type="button" onClick={() => toggleUsarWebFoto(fotosLightboxLista[lightboxFotoIdx])}
                      style={{
                        padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12,
                        background: fotosLightboxLista[lightboxFotoIdx].usar_web === false ? '#ef4444' : 'rgba(255,255,255,0.92)',
                        color: fotosLightboxLista[lightboxFotoIdx].usar_web === false ? '#fff' : '#111',
                      }}>
                      {fotosLightboxLista[lightboxFotoIdx].usar_web === false ? 'Excluida de Web' : 'No usar en Web'}
                    </button>
                    <button type="button" onClick={() => toggleMarcoFoto(fotosLightboxLista[lightboxFotoIdx])}
                      style={{
                        padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12,
                        background: fotosLightboxLista[lightboxFotoIdx].marco_autoenhance === false ? '#64748b' : '#8b5cf6',
                        color: '#fff',
                      }}>
                      Editar: {fotosLightboxLista[lightboxFotoIdx].marco_autoenhance === false ? 'OFF' : 'ON'}
                    </button>
                    {lightboxModoFotos === 'marco' && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            const foto = fotosLightboxLista[lightboxFotoIdx];
                            const nextLen = fotosLightboxLista.length - 1;
                            quitarDeSeleccionMarco(foto);
                            if (nextLen <= 0) cerrarLightboxFoto();
                            else setLightboxFotoIdx((i) => Math.min(i, nextLen - 1));
                          }}
                          style={{
                            padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12,
                            background: '#ef4444', color: '#fff',
                          }}
                        >
                          Eliminar de selección
                        </button>
                      </>
                    )}
                      </>
                    )}
                  </div>
                )}
                {lightboxFotoSoloConsulta && fotosLightboxLista[lightboxFotoIdx]?.id && (
                  <button
                    type="button"
                    title="Ver metadatos de la foto"
                    onClick={(e) => { e.stopPropagation(); abrirMetadatosFoto(fotosLightboxLista[lightboxFotoIdx]); }}
                    style={{
                      position: 'absolute', bottom: 56, left: '50%', transform: 'translateX(-50%)',
                      padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12,
                      background: 'rgba(14,165,233,0.95)', color: '#fff', zIndex: 3,
                    }}
                  >
                    i Metadatos
                  </button>
                )}
                <div style={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', color: '#fff', fontSize: 12, fontWeight: 600, textShadow: '0 1px 4px #000' }}>
                  {lightboxFotoIdx + 1} / {fotosLightboxLista.length}
                </div>
              </>
            )}
          </div>
        , document.body)}

        {fotoMetaPanel && createPortal(
          <div
            role="dialog"
            aria-label="Metadatos de la foto"
            onClick={() => setFotoMetaPanel(null)}
            style={{
              /* Por encima del lightbox de fotos (zIndex 12000) */
              position: 'fixed', inset: 0, zIndex: 14000,
              background: 'rgba(15,23,42,0.55)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 16,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 'min(560px, 96vw)', maxHeight: '86vh', overflow: 'auto',
                background: isDark ? '#0f172a' : '#fff',
                color: isDark ? '#e2e8f0' : '#0f172a',
                borderRadius: 14, border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                boxShadow: '0 20px 50px rgba(0,0,0,0.35)', padding: '16px 18px 20px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Metadatos de la foto</h3>
                <button
                  type="button"
                  onClick={() => setFotoMetaPanel(null)}
                  style={{
                    width: 32, height: 32, borderRadius: 8, border: 'none', cursor: 'pointer',
                    background: isDark ? '#1e293b' : '#f1f5f9', color: 'inherit', fontWeight: 800, fontSize: 16,
                  }}
                >✕</button>
              </div>
              {fotoMetaPanel.loading && (
                <p style={{ margin: 0, fontSize: 13, opacity: 0.8 }}>Leyendo EXIF y datos de la imagen…</p>
              )}
              {fotoMetaPanel.error && (
                <p style={{ margin: 0, fontSize: 13, color: '#ef4444' }}>{fotoMetaPanel.error}</p>
              )}
              {fotoMetaPanel.data && (() => {
                const d = fotoMetaPanel.data;
                const muted = isDark ? '#94a3b8' : '#64748b';
                const filaStyle = {
                  padding: '5px 8px 5px 0', verticalAlign: 'top', fontWeight: 700,
                  color: muted, whiteSpace: 'nowrap', width: '38%',
                };
                const renderTabla = (filas, keyPrefix = '') => (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <tbody>
                      {filas.map(([k, v]) => (
                        <tr key={`${keyPrefix}${k}`}>
                          <td style={filaStyle}>{k}</td>
                          <td style={{ padding: '5px 0', verticalAlign: 'top', wordBreak: 'break-word' }}>{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
                const push = (arr, label, value) => {
                  if (value == null || value === '') return;
                  arr.push([label, typeof value === 'object' ? JSON.stringify(value) : String(value)]);
                };

                const r = d.resumen || {};
                const filasCamara = [];
                push(filasCamara, 'Cámara', r.camara || [r.fabricante, r.modelo].filter(Boolean).join(' '));
                push(filasCamara, 'Calidad / modo', r.calidad_captura);
                push(filasCamara, 'Megapíxeles', r.megapixeles);
                push(filasCamara, 'Resolución', r.resolucion);
                push(filasCamara, 'Software', r.software);
                push(filasCamara, 'Fecha captura', r.fecha_captura
                  ? (() => {
                    try { return new Date(r.fecha_captura).toLocaleString('es-ES'); }
                    catch { return String(r.fecha_captura); }
                  })()
                  : null);
                push(filasCamara, 'Zona horaria', r.zona_horaria);
                push(filasCamara, 'Programa exposición', r.programa_exposicion);
                push(filasCamara, 'Modo exposición', r.modo_exposicion);
                push(filasCamara, 'ISO', r.iso);
                push(filasCamara, 'Apertura', r.apertura);
                push(filasCamara, 'Obturación', r.obturacion);
                push(filasCamara, 'Focal', r.focal_mm);
                push(filasCamara, 'Focal (35 mm equiv.)', r.focal_35mm);
                push(filasCamara, 'Flash', r.flash);
                push(filasCamara, 'Balance blancos', r.balance_blancos);
                push(filasCamara, 'Compensación EV', r.compensacion_ev);
                push(filasCamara, 'HDR', r.hdr);
                push(filasCamara, 'Zoom / lente', r.zoom || (r.zoom_factor != null ? `×${r.zoom_factor}` : null) || r.lente);
                push(filasCamara, 'Sensor', r.sensor);
                push(filasCamara, 'OpMode (móvil)', r.op_mode);
                if (r.gps) {
                  push(filasCamara, 'GPS', `${r.gps.latitud}, ${r.gps.longitud}`);
                  if (r.gps.altitud_m != null) push(filasCamara, 'Altitud', `${r.gps.altitud_m} m`);
                } else if (r.gps_vacio) {
                  push(filasCamara, 'GPS', 'El móvil no dejó coordenadas (privacidad / desactivado)');
                }

                const filasCrm = [];
                push(filasCrm, 'Archivo', d.nombre_archivo);
                push(filasCrm, 'Orden', d.orden != null ? Number(d.orden) + 1 : null);
                push(filasCrm, 'ID', d.id);
                push(filasCrm, 'Cliente local_id', d.cliente_foto_id);
                push(filasCrm, 'Subida CRM', d.created_at ? new Date(d.created_at).toLocaleString('es-ES') : null);
                push(filasCrm, 'Usar en web', d.usar_web === false ? 'No' : 'Sí');
                push(filasCrm, 'Editar Marco', d.marco_autoenhance === false ? 'OFF' : 'ON');
                push(filasCrm, 'En selección Marco', d.en_seleccion_marco ? 'Sí' : 'No');
                if (d.imagen) {
                  push(filasCrm, 'Tamaño archivo', d.imagen.bytes_legible || d.imagen.bytes);
                  push(filasCrm, 'Formato', d.imagen.formato);
                  push(filasCrm, 'Resolución', (d.imagen.ancho && d.imagen.alto) ? `${d.imagen.ancho} × ${d.imagen.alto}` : null);
                }
                if (d.nota) push(filasCrm, 'Nota', d.nota);

                const exifEntries = d.exif && typeof d.exif === 'object'
                  ? Object.entries(d.exif).filter(([k]) => k !== '_aviso')
                  : [];

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {filasCamara.length > 0 && (
                      <section>
                        <h4 style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 800 }}>
                          Original (cámara / ajustes / ubicación)
                        </h4>
                        {renderTabla(filasCamara, 'cam-')}
                        {r.gps?.maps_url && (
                          <a
                            href={r.gps.maps_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ display: 'inline-block', marginTop: 8, fontSize: 12, fontWeight: 700, color: '#0ea5e9' }}
                          >
                            Ver ubicación en Maps
                          </a>
                        )}
                      </section>
                    )}
                    {!filasCamara.length && (
                      <p style={{ margin: 0, fontSize: 12, color: muted }}>
                        Esta foto no trae EXIF de cámara (se pudo perder al convertir HEIC o al girar).
                      </p>
                    )}
                    <section>
                      <h4 style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 800 }}>En el CRM</h4>
                      {renderTabla(filasCrm, 'crm-')}
                    </section>
                    {d.url && (
                      <a
                        href={d.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: 12, fontWeight: 700, color: '#0ea5e9' }}
                      >
                        Abrir original
                      </a>
                    )}
                    {exifEntries.length > 0 && (
                      <details>
                        <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 800 }}>
                          EXIF completo ({exifEntries.length} campos)
                        </summary>
                        <div style={{ marginTop: 8 }}>
                          {renderTabla(
                            exifEntries.map(([k, v]) => [k, typeof v === 'object' ? JSON.stringify(v) : String(v)]),
                            'exif-',
                          )}
                        </div>
                      </details>
                    )}
                    {d.exif?._aviso && (
                      <p style={{ margin: 0, fontSize: 11, opacity: 0.75 }}>{d.exif._aviso}</p>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        , document.body)}

        {insertarMarcoOpen && createPortal(
          <div
            role="presentation"
            onClick={() => { setInsertarMarcoOpen(false); setInsertarPreviewIdx(null); }}
            style={{
              position: 'fixed', inset: 0, zIndex: 12100, background: 'rgba(0,0,0,0.94)',
              display: 'flex', flexDirection: 'column', padding: 16, overflow: 'auto',
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12 }}
            >
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>
                Insertar en selección Marco ({fotosFueraSeleccionMarco.length} disponibles
                {fotosFueraSeleccionMarco.some((f) => esFotoRawSinPreview(f))
                  ? ` · ${fotosFueraSeleccionMarco.filter((f) => esFotoRawSinPreview(f)).length} RAW sin vista previa`
                  : ''}
                )
              </span>
              <button
                type="button"
                onClick={() => { setInsertarMarcoOpen(false); setInsertarPreviewIdx(null); }}
                style={{
                  padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
                }}
              >Cerrar</button>
            </div>
            {insertarPreviewIdx != null && fotosFueraSeleccionMarco[insertarPreviewIdx] && (
              <div
                onClick={(e) => e.stopPropagation()}
                style={{ marginBottom: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}
              >
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                  {fotosFueraSeleccionMarco.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setInsertarPreviewIdx((i) => (i > 0 ? i - 1 : fotosFueraSeleccionMarco.length - 1))}
                      style={{
                        position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2,
                        width: 40, height: 40, borderRadius: '50%', border: 'none', cursor: 'pointer', fontSize: 20, color: '#111',
                        background: 'rgba(255,255,255,0.9)', fontWeight: 700,
                      }}
                    >‹</button>
                  )}
                  <CaptacionFotoThumb
                    foto={fotosFueraSeleccionMarco[insertarPreviewIdx]}
                    mode="full"
                    style={{
                      maxWidth: '88vw',
                      maxHeight: '50vh',
                      minWidth: 280,
                      minHeight: 180,
                      borderRadius: 8,
                      objectFit: 'contain',
                    }}
                    imgStyle={{ objectFit: 'contain', maxWidth: '88vw', maxHeight: '50vh' }}
                  />
                  {fotosFueraSeleccionMarco.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setInsertarPreviewIdx((i) => (i < fotosFueraSeleccionMarco.length - 1 ? i + 1 : 0))}
                      style={{
                        position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 2,
                        width: 40, height: 40, borderRadius: '50%', border: 'none', cursor: 'pointer', fontSize: 20, color: '#111',
                        background: 'rgba(255,255,255,0.9)', fontWeight: 700,
                      }}
                    >›</button>
                  )}
                </div>
                <span style={{ color: '#94a3b8', fontSize: 12, fontWeight: 600 }}>
                  {insertarPreviewIdx + 1} / {fotosFueraSeleccionMarco.length}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => insertarEnSeleccionMarco(fotosFueraSeleccionMarco[insertarPreviewIdx])}
                    style={{
                      padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12,
                      background: '#8b5cf6', color: '#fff',
                    }}
                  >Insertar esta</button>
                  <button
                    type="button"
                    onClick={() => setInsertarPreviewIdx(null)}
                    style={{
                      padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12,
                      background: 'rgba(255,255,255,0.92)', color: '#111',
                    }}
                  >Volver a miniaturas</button>
                </div>
              </div>
            )}
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'flex-start' }}
            >
              {fotosFueraSeleccionMarco.length === 0 && (
                <span style={{ color: '#94a3b8', fontSize: 13 }}>Todas las fotos ya están en la selección Marco.</span>
              )}
              {fotosFueraSeleccionMarco.map((foto, idx) => {
                const activa = idx === insertarPreviewIdx;
                return (
                <div
                  key={foto.id || `ins-${idx}`}
                  ref={(el) => { insertarThumbRefsRef.current[idx] = el; }}
                  style={{
                    position: 'relative', width: 148, height: 148, borderRadius: 10, overflow: 'hidden',
                    border: activa ? '3px solid #8b5cf6' : '1px solid rgba(255,255,255,0.2)',
                    boxShadow: activa ? '0 0 0 3px rgba(139,92,246,0.35), 0 4px 16px rgba(139,92,246,0.5)' : 'none',
                    background: '#0f172a',
                  }}
                >
                  <CaptacionFotoThumb
                    foto={foto}
                    style={{ width: '100%', height: '100%' }}
                  />
                  {activa && (
                    <span style={{
                      position: 'absolute', top: 6, left: 6, padding: '2px 7px', borderRadius: 6,
                      fontSize: 9, fontWeight: 800, background: '#8b5cf6', color: '#fff',
                      pointerEvents: 'none',
                    }}>
                      Viendo
                    </span>
                  )}
                  <button
                    type="button"
                    title="Ver en grande"
                    onClick={() => setInsertarPreviewIdx(idx)}
                    style={{
                      position: 'absolute', top: 6, right: 6, width: 32, height: 32, borderRadius: 8,
                      border: 'none', cursor: 'pointer', background: 'rgba(0,0,0,0.65)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                    }}
                  >
                    <IconoLupa size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => insertarEnSeleccionMarco(foto)}
                    style={{
                      position: 'absolute', bottom: 6, left: 6, right: 6, padding: '6px 8px', borderRadius: 6,
                      border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 11,
                      background: '#8b5cf6', color: '#fff',
                    }}
                  >
                    Insertar
                  </button>
                </div>
                );
              })}
            </div>
          </div>
        , document.body)}

        {visorVideoUrl && createPortal(
          <div
            role="presentation"
            onClick={() => { setVisorVideoUrl(null); setVisorVideoError(null); }}
            style={{
              position: 'fixed', inset: 0, zIndex: 12000, background: 'rgba(0,0,0,0.92)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
            }}
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setVisorVideoUrl(null); setVisorVideoError(null); }}
              style={{
                position: 'absolute', top: 16, right: 16, padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                background: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
              }}
            >Cerrar</button>
            {visorVideoUrl === '__pending__' ? (
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  maxWidth: 420, padding: '28px 24px', borderRadius: 14, textAlign: 'center',
                  background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)',
                }}
              >
                <div style={{ fontSize: 36, marginBottom: 12 }}>⏳</div>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Vídeo subiéndose…</div>
                <div style={{ fontSize: 13, opacity: 0.85, lineHeight: 1.45 }}>
                  El archivo todavía se está enviando. Cuando termine la subida podrás reproducirlo aquí.
                </div>
              </div>
            ) : visorVideoError ? (
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  maxWidth: 420, padding: '28px 24px', borderRadius: 14, textAlign: 'center',
                  background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)',
                }}
              >
                <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>No se puede reproducir</div>
                <div style={{ fontSize: 13, opacity: 0.85, lineHeight: 1.45 }}>
                  Este clip usa un formato que el navegador no soporta (p. ej. HEVC del móvil).
                  Se está convirtiendo a MP4 compatible; vuelve a abrir en unos minutos.
                </div>
              </div>
            ) : (
              <video
                ref={visorVideoRef}
                src={visorVideoUrl}
                controls
                playsInline
                autoPlay
                onClick={(e) => e.stopPropagation()}
                onError={() => setVisorVideoError(true)}
                style={{ maxWidth: '96vw', maxHeight: '88vh', borderRadius: 8 }}
              />
            )}
          </div>
        , document.body)}

        {docPreview && createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Vista previa del documento"
            onClick={() => setDocPreview(null)}
            style={{
              position: 'fixed', inset: 0, zIndex: 13000, background: 'rgba(0,0,0,0.82)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 'min(96vw, 1100px)', height: 'min(92vh, 860px)', display: 'flex', flexDirection: 'column',
                borderRadius: 10, overflow: 'hidden', background: c.bgCard, border: `1px solid ${c.border}`,
                boxShadow: '0 20px 60px rgba(0,0,0,0.45)',
              }}
            >
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                padding: '8px 12px', borderBottom: `1px solid ${c.border}`, background: c.bgPanel || c.bgCard, flexShrink: 0,
              }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {docPreview.title}
                </span>
                <button
                  type="button"
                  onClick={() => setDocPreview(null)}
                  style={{
                    flexShrink: 0, padding: '4px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                    background: c.border, color: c.textPrimary, fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                  }}
                >Cerrar</button>
              </div>
              <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', padding: 8, background: c.bgApp, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {docPreview.kind === 'image' && (
                  <VisorImagenDocumento src={docPreview.src} title={docPreview.title} />
                )}
                {docPreview.kind === 'pdf' && (
                  <iframe
                    key={docPreview.src}
                    title={docPreview.title}
                    src={docPreview.src}
                    style={{ width: '100%', height: '100%', minHeight: '70vh', border: `1px solid ${c.border}`, borderRadius: 6, background: '#fff' }}
                  />
                )}
                {docPreview.kind === 'other' && (
                  <div style={{ textAlign: 'center', padding: '24px 16px', maxWidth: 420 }}>
                    <p style={{ fontSize: 13, color: c.textMuted, marginBottom: 16 }}>
                      No hay vista previa integrada para este formato. Puedes abrirlo en una pestaña nueva.
                    </p>
                    <a
                      href={docPreview.src}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: ml.purple, fontWeight: 600, fontSize: 13 }}
                    >Abrir documento</a>
                  </div>
                )}
              </div>
            </div>
          </div>
        , document.body)}

        {/* Footer */}
        <div style={{ flexShrink: 0, borderTop: `1px solid ${c.border}` }}>

          {/* Panel motivo archivado */}
          {accionArchivo && (
            <div style={{ padding: '14px 24px', borderBottom: `1px solid ${c.border}`, background: accionArchivo === 'frio' ? (isDark ? 'rgba(59,130,246,0.08)' : '#eff6ff') : (isDark ? 'rgba(239,68,68,0.08)' : '#fef2f2') }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: accionArchivo === 'frio' ? '#3b82f6' : '#ef4444', marginBottom: 8 }}>
                {accionArchivo === 'frio' ? '❄️ Marcar como frío' : '🗑️ Eliminar captación'} — Motivo (opcional)
              </div>
              <textarea value={motivoArchivo} onChange={e => setMotivoArchivo(e.target.value)}
                placeholder="Ej: No llegan a acuerdo en precio, propietario desiste..."
                rows={2} style={{ ...inputStyle, resize: 'none', marginBottom: 10, lineHeight: 1.5 }}
                onFocus={e => e.target.style.borderColor = accionArchivo === 'frio' ? '#3b82f6' : '#ef4444'}
                onBlur={e => e.target.style.borderColor = c.border} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => { setAccionArchivo(null); setMotivoArchivo(''); }} style={{
                  flex: 1, padding: '9px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                  border: `1px solid ${c.border}`, background: 'transparent', color: c.textMuted,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}>Cancelar</button>
                <button onClick={handleArchivar} disabled={archivando} style={{
                  flex: 2, padding: '9px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                  border: 'none', cursor: archivando ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                  background: accionArchivo === 'frio' ? '#3b82f6' : '#ef4444', color: '#fff',
                  opacity: archivando ? 0.6 : 1,
                }}>
                  {archivando ? 'Procesando...' : accionArchivo === 'frio' ? '❄️ Confirmar frío' : '🗑️ Confirmar eliminación'}
                </button>
              </div>
            </div>
          )}

          <div style={{ padding: '14px 20px 18px', display: 'flex', gap: 8, alignItems: 'center', borderTop: `1px solid ${isDark ? c.border : '#a8a8a0'}`, background: isDark ? 'transparent' : '#e0e0d8' }}>
            {!accionArchivo && (
              <>
                {verRevFrio && !capActiva.propiedad_id && !capActiva.fecha_validacion && (
                <button onClick={() => { setAccionArchivo('frio'); setMotivoArchivo(''); }} style={{
                  padding: '9px 14px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                  border: `1px solid #3b82f640`, background: isDark ? 'rgba(59,130,246,0.08)' : '#eff6ff',
                  color: '#3b82f6', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                }}>❄️ Frío</button>
                )}
                {verRevEliminar && (
                <button onClick={() => { setAccionArchivo('eliminado'); setMotivoArchivo(''); }} style={{
                  padding: '9px 14px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                  border: `1px solid #ef444440`, background: isDark ? 'rgba(239,68,68,0.08)' : '#fef2f2',
                  color: '#ef4444', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                }}>🗑️ Eliminar</button>
                )}
              </>
            )}
            <div style={{ flex: 1 }} />
            <button onClick={() => { if (!guardandoAlgo) solicitarCierreRevisar(); }} style={{
              padding: '11px 24px', borderRadius: 10, border: `1.5px solid ${isDark ? c.border : '#a8a8a0'}`,
              background: isDark ? c.bgInput : '#ffffff', color: c.textPrimary,
              cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
            }}>Cancelar</button>
          </div>
        </div>
      </div>
    </div>

    <ModalPortalesAltaWeb
      isOpen={modalPortalesAltaOpen}
      initialSlugs={
        Array.isArray(valoresExtras.portales_alta_slugs)
          ? valoresExtras.portales_alta_slugs
          : (Array.isArray(extrasCap.portales_alta_slugs) ? extrasCap.portales_alta_slugs : null)
      }
      onClose={() => setModalPortalesAltaOpen(false)}
      onConfirm={async ({ soloWeb, slugs }) => {
        const list = Array.isArray(slugs) ? slugs.map((s) => String(s).toLowerCase()) : [];
        if (!list.includes("sooprema")) list.push("sooprema");
        const otros = list.filter((s) => s !== "sooprema");
        const patch = {
          publicar_web: true,
          portales_alta_slugs: list,
          // Pendiente si hay portales además de Web (Idealista/Fotocasa/…)
          portales_alta_pendiente: !soloWeb && otros.length > 0,
          portales_alta_aplicada: false,
        };
        setValoresExtras((prev) => ({ ...prev, ...patch }));
        await api.patch(`/captaciones/${captacion.id}/datos-extras`, {
          datos: patch,
          solo_borrador: true,
        });
      }}
    />
  </>
  );
}